import { LitElement, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { keyed } from "lit/directives/keyed.js";
import { arrangementDrop, arrangementFits, placedInPile, renderArrangement, suggestedPiles } from "./arrangement";
import { freshOrdering, renderTriggerOrder, type TriggerOrdering } from "./trigger-order";
import type {
  Action, AlwaysAnswer, Attack, AttackerGroup, AutoAnswer, Block, BlockOptions, ChoiceResult, DamageAssignment, HistoryEntry, ManaPayment, ManaSource, ManaTap,
  Keyword, Offer, OptionalChoice, Pacing, PregamePlayer, RepeatableLoop,
  Moved, PublicAbilityItem, PublicArrangement, PublicTrigger, PublicCard, PublicChoiceSpec, PublicDecision, PublicFaces, PublicPlayer, PublicRefusal, PublicState, StopRow, Stops, Tapper, Target, TapPayment, TurnStep,
  Undoable, Look,
} from "../protocol";
import { cursors, palette } from "../theme";
import { playerPortraitAt } from "../player-avatar";
import { arrowLayer, measureArrows, sameArrows, type ArrowSpec, type DrawnArrow, type Point } from "./arrows";
import { boardStyles } from "./board-styles";
import { battlefieldUrl, cardBackStyle, cardBackStyles, namePlateClass, namePlateStyles, type Battlefield, type SeatLooks } from "../cosmetics";
import { playSound } from "../sounds";
import { renderKeywordBadges } from "./keyword-icons";
import { fly, land, measureTakeoffs, type Takeoff } from "./moves";
import { tableScale } from "./table-scale";
import { LookSender, lookAnchors, lookOf, lookedHandBack, type Looks, type PointedZone } from "./looks";
import { IMPACT_MS, damageFloaters, lifeChanges, lifeFloaters, measureStrikes, newDamage, playStrikes, pulseLife, type Floater, type Strike } from "./strikes";
import { playerClock, type ClockReading } from "./clock";
import { TILE_RATIO, attachmentsOf, cardsIn, frameOf, groupField, isSummoningSick, type GroupedField, type PermGroup, wrappedRowFit } from "./field";
import {
  MANA_ORDER, PHASE_LABEL, STOP_STEPS, actionHowLabel, actionLabel, actionPrimaryOid, altCastLabel, cardLabel, chosenLabel, combatLabel, controllerOf, costText,
  findCardAnywhere, findOnAnyBattlefield, isCreatureCard, isFaceDown, isPass, isPregameDecision, knownFace, latestLines, loggedName, loggedTurns, lookupCardNameAnywhere,
  manaText, nextStepLabel, optionValue, passedSincePriorityMoved, passingLabel, passingNow, playerName, priorityOrder,
  ptLabelOf, sameAbility, scryfallSearch, sameStep, sentence, sourceCostHint, spendOnlyHint, stackItemCard, stackItemController, stackItemKind, stackItemName,
  stackItemObjectId, stackItemTargets, stepOf, targetAnchor, targetNameLabel, toggledStep, typeLineOf, waitingLabel, type LogLine,
} from "./game-text";
import { OracleCache } from "./oracle";
import { renderManaPips } from "./mana-pips";
import { faceName, facesOf, otherSide, playsCard, renderFacePicker, sideLabel, upSide } from "./faces";
import { NameLists, isNameQuestion, nameQuestionNoun, nameQuestionTitle, type NameQuestion } from "./name-picker";
import { abilityLine, abilityLines, renderAbilityCard, renderChooseOne, renderChoiceList, renderOption } from "./choose-one";
import {
  aimAt, aimCandidates, aimComplete, aimedAt, aimedWithout, isCandidate, isPicked, objectTarget, offerAction, playerTarget, takesTargets, wayOf, waysToUse, type Aim,
} from "./targeting";
import {
  attackingWithAll, blockAsks, canAttack, canAttackAt, canBlock, firstDefender, isAttackable, isAttackablePlaneswalker, isAttackablePlayer,
  damageInOrder, isBlocker, movedInOrder, withAttack, withBlock, withoutAttack, withoutBlock, type Defender,
} from "./combat";

// What the board shows: one viewer's projection and what the Room told them besides.
export type BoardView = {
  game: string;
  seat: string;
  state: PublicState;
  pacing: Pacing | null;
  history: HistoryEntry[];
  // The cards the viewer saw change zones in the latest StateUpdate, oldest first; a new array with
  // each update, which the board flies in once (./moves).
  moves: Moved[];
  error: string | null;
  autoPay: boolean;
  // While auto-pay's answer is on its way its payment question isn't shown; a refusal shows it.
  autoPaying: boolean;
  // The AI seats' levels, by seat.
  aiLevels: { [seat: string]: string };
  // A newer update waits while this one shows an AI seat's action; a press shows it (./paced-updates).
  catchingUp: boolean;
  // A step the Room passed through on the way to the next update, which the board shows instead of
  // the state's; null for the state's own.
  walking: TurnStep | null;
  // How long a new turn's banner ("Medium AI's turn") shows; 0 shows none.
  turnBannerMs: number;
  // The life each player started with (103.4): 40 in Commander (903.7), else 20.
  startingLife: number;
  // What the viewer may undo, as the Room last said; null until it has.
  undoable: Undoable | null;
  // Every player's chess clock; null until the Room has sent them.
  clocks: ClockReading | null;
  // Each seat's mulligans and whether it has kept its opening hand.
  pregame: PregamePlayer[];
  // Where the hovered card's preview shows; "auto" is the side away from the pointer.
  previewAt: PreviewAt;
  looks: Looks;
  // The card back, card frame and name plate each seat's player chose, by seat; a seat without one
  // has the table's own.
  seatLooks: { [seat: string]: SeatLooks };
  // The battlefield the viewer chose to play on, which only they see.
  battlefield: Battlefield | null;
};

export type PreviewAt = "right" | "left" | "middle" | "auto";

// What the viewer can do at the board. A board given none is read-only: it shows the game and
// sends nothing (spectating, #255).
export interface BoardIntents {
  takeAction(action: Action, holdingPriority: boolean): void;
  endTurn(turn: number): void;
  skipTurn(turn: number): void;
  resolveStack(): void;
  repeatLoop(times: number): void;
  setStops(stops: Stops): void;
  setAutoPassCards(names: string[]): void;
  setAutoAnswers(answers: AutoAnswer[]): void;
  answerChoice(answer: ChoiceResult): void;
  declareAttackers(attacks: Attack[]): void;
  declareBlockers(blocks: Block[]): void;
  setAutoPay(on: boolean): void;
  undoLastAction(): void;
  undoToLastPass(): void;
  dismissError(): void;
  // Concedes a game still going, and goes home.
  leaveGame(): void;
  chooseStartingPlayer(seat: string): void;
  keepHand(): void;
  mulligan(): void;
  bottomCards(oids: number[]): void;
  lookAt(look: Look): void;
}

type Declaring =
  | { kind: "attackers"; groups: AttackerGroup[] }
  | { kind: "blockers"; options: BlockOptions };

type ZoneShown = { pid: string; zone: "graveyard" | "exile" };

const zoneCaption = (name: string, count: number): TemplateResult =>
  html`<span class="zone-plate"><span class="zone-name">${name}</span><span class="zone-count">${count}</span></span>`;
type LookRing = { key: string; x: number; y: number; w: number; h: number; name: string | null };

function sameRings(a: LookRing[], b: LookRing[]): boolean {
  return a.length === b.length && a.every((r, i) => {
    const o = b[i]!;
    return r.key === o.key && Math.abs(r.x - o.x) < 1 && Math.abs(r.y - o.y) < 1 && Math.abs(r.w - o.w) < 1 && Math.abs(r.h - o.h) < 1 && r.name === o.name;
  });
}

type DragKind = "aim" | "attack" | "block" | "hand" | "damage" | "arrange";

// What the pregame lays across the dimmed table for the viewer to answer: the coin flip's choice, or
// their opening hand until they keep it.
type PregameLayout =
  | { kind: "coin-flip"; seats: string[] }
  | { kind: "opening-hand"; cards: PublicCard[] };

type Pace = { label: string; hint: string; run: (() => void) | null; waiting?: boolean };

const ON_MAC = /Mac|iPhone|iPad/.test(navigator.userAgent);
const UNDO_KEY = ON_MAC ? "⌘Z" : "Ctrl+Z";
const HOLD_KEY = ON_MAC ? "⌘" : "Ctrl";
// A card's height over its width (63 × 88 mm).
const CARD_RATIO = 88 / 63;
// Planeswalkers stand two to a line, wrapping into more lines that share the battlefield's height,
// none taller than both rows together, and never so wide that keeping clear of them at both ends
// takes more than half its width.
function walkerGrid(count: number, space: { height: number; width: number; rows: number }): { cols: number; w: number; h: number } {
  const lines = Math.ceil(count / 2);
  const cols = Math.min(count, 2);
  const tallest = Math.min(space.rows, Math.floor((space.height - 10 * (lines - 1)) / lines));
  const w = Math.min(Math.round(tallest / CARD_RATIO), Math.floor(space.width / (4 * cols)) - 10);
  return { cols, w, h: Math.round(w * CARD_RATIO) };
}
// A hand card's width, in pixels: never under the least, never over the most, and otherwise this
// share of the window's width, as on Arena; and how much of each card the next leaves uncovered.
const HAND_CARD_LEAST_W = 120;
const HAND_CARD_MOST_W = 200;
const HAND_CARD_SCREEN_SHARE = 0.12;
const HAND_CARD_UNCOVERED = 0.88;
const PREVIEW_ZOOM_MAX = 2.5;
const PREVIEW_ZOOM_PER_PIXEL = 0.002;
const FLOATER_MS = 1300;
// The least room the table lays out in; a smaller screen gets it scaled down.
const TABLE_MIN_W = 1000;
const TABLE_MIN_H = 600;
// Below this virtual height (a phone held sideways) the viewer's portrait shrinks to give the board room.
const COMPACT_H = 720;
type SideButton = { label: string; title: string; name?: string; kbd?: string; run: () => void; disabled?: boolean };

@customElement("arena-board")
export class ArenaBoard extends LitElement {
  @property({ attribute: false }) view!: BoardView;
  @property({ attribute: false }) intents: BoardIntents | null = null;
  // An arrangement question's piles so far, kept with the question they answer.
  @state() private arranged: { question: object; piles: number[][] } | null = null;
  @state() private ordering: TriggerOrdering | null = null;
  private triggerPlan: string[] = [];
  private triggerPlanTurn = -1;
  private planAnsweredIn: PublicTrigger[] | null = null;
  @state() private peekingAt: PublicChoiceSpec | null = null;

  @state() private hoverOid: number | null = null;
  // A card the log names, as it was then: the preview's fallback once it has left where it was.
  private hoverLogged: PublicCard | null = null;
  // Where "auto" shows the preview: away from the pointer.
  @state() private previewSide: "at-left" | "at-right" = "at-right";
  @state() private previewZoom = 1;
  // The table's layout size and scale when the screen is smaller than the table's minimum.
  @state() private fit: { w: number; h: number; s: number } | null = null;
  private readonly sizer = new ResizeObserver(() => this.refit());
  // A card with more than one way to use it: each way's legal actions.
  @state() private cardMenu: { oid: number; ways: Offer[][] } | null = null;
  @state() private hiddenChoice: string | null = null;
  @state() private aim: Aim | null = null;
  // Where a press on a card started, while it may become a drag: onto a target, a defender to
  // attack, or an attacker to block.
  private pressed: { oid: number; x: number; y: number; kind: DragKind } | null = null;
  @state() private dragging: { oid: number; kind: DragKind } | null = null;
  private dragEnded = false;
  private handDragOrigin: { index: number; rotation: number; offsetY: number } | null = null;
  private handOrder: number[] = [];
  private handOrderGame: string | null = null;
  // The declaration being built: the attacks and blocks so far, where a clicked attacker goes, and
  // the creature picked to block, whose attacker is clicked next.
  @state() private attackPlan: Attack[] = [];
  @state() private attackTo: Defender | null = null;
  @state() private blockPlan: Block[] = [];
  @state() private pickedBlocker: number | null = null;
  @state() private damageOrder: number[] = [];
  @state() private viewingBattlefield = false;
  @state() private payPicks: ManaTap[] = [];
  // The creatures picked to tap for a crew cost (702.122a).
  @state() private tapPicks: number[] = [];
  // The opening hand's cards picked to go on the bottom of the library (103.5).
  @state() private bottomPicks: Set<number> = new Set();
  // The name the name picker's text names, if any.
  @state() private namePick: string | null = null;
  @state() private holdNext = false;
  @state() private moreOpen = false;
  // The prompt to take a loop the viewer has just gone round round again, while it's open.
  @state() private loopPromptOpen = false;
  // A new turn's banner while it shows: the turn and whose it is.
  @state() private turnBanner: TurnStep | null = null;
  // The turn the board last showed, so that a later one is announced.
  private shownTurn: number | null = null;
  private bannerTimer: number | null = null;
  @state() private logOpen = false;
  @state() private menuOpen = false;
  @state() private stopsOpen = false;
  @state() private zoneShown: ZoneShown | null = null;
  // The stack object under the pointer, whose arrows stand out, and the one whose auto-pass menu is open.
  @state() private hoverStack: number | null = null;
  @state() private stackMenu: number | null = null;
  @state() private logMenu: { card: PublicCard; x: number; y: number } | null = null;
  @state() private copiedLogCard: PublicCard | null = null;
  // A card of the viewer's they clicked but can't use now, whose reasons show until the board moves on.
  @state() private whyShown: number | null = null;
  @state() private arrows: DrawnArrow[] = [];
  @state() private lookRings: LookRing[] = [];
  @state() private floaters: Floater[] = [];
  private strikes: Strike[] = [];
  private lifeDeltas: { pid: string; delta: number }[] = [];
  @state() private pointedZone: PointedZone | null = null;
  @state() private pointedSeat: string | null = null;
  private lookSender: LookSender | null = null;
  // The pointer, while an arrow follows it.
  @state() private cursor: Point | null = null;

  private ctrlHeld = false;
  // A previewed card is its picture, and any reasons the viewer can't use it; held, Alt writes out
  // its text and state beneath.
  @state() private altHeld = false;
  // A press outside a popup cancels it, and the click it ends with does nothing else.
  private swallowClick = false;
  // The state a question's Cancel was sent in, so a second press before the Room answers sends no
  // second undo.
  private cancelSentIn: PublicState | null = null;
  private readonly oracle = new OracleCache(() => this.requestUpdate());
  private readonly nameLists = new NameLists(() => this.requestUpdate());
  private settleTimer: number | null = null;
  // The battlefields' sizes as last measured: planeswalkers are drawn to fit them.
  private fieldSizes = "";
  // The latest update's moved cards as they were drawn before it, and their flights once it's drawn.
  private takeoffs: Takeoff[] = [];
  private flights: Animation[] = [];
  // What another player's action put onto a battlefield or the stack in the latest update, which
  // glows as it arrives.
  private arrived: Set<number> = new Set();

  static styles = [palette, cursors, boardStyles, cardBackStyles, namePlateStyles];

  // Any key or press lands the cards in flight, so a flight never stands between a player and the board.
  private readonly onPointerDown = (e: PointerEvent): void => {
    this.landFlights();
    this.swallowClick = false;
    if (this.moreOpen && !e.composedPath().some((t) => t instanceof Element && t.matches(".second"))) this.moreOpen = false;
    if (this.logMenu && !e.composedPath().some((t) => t instanceof Element && t.matches(".log-menu"))) this.logMenu = null;
    if (e.button !== 0 || !this.pressedOutsidePop(e) || !this.cancelPop()) return;
    e.stopPropagation();
    this.swallowClick = true;
  };
  private readonly onWheel = (e: WheelEvent): void => {
    if (this.hoverOid === null || e.deltaY === 0 || e.ctrlKey || this.wheelScrollsUnder(e)) return;
    const preview = this.renderRoot.querySelector<HTMLElement>(".card-preview:not(.asking)");
    if (!preview) return;
    e.preventDefault();
    const unzoomedHeight = preview.getBoundingClientRect().height / this.previewZoom;
    const fits = Math.max(1, (this.clientHeight - 40) / unzoomedHeight);
    const zoom = this.previewZoom * Math.exp(-e.deltaY * PREVIEW_ZOOM_PER_PIXEL);
    this.previewZoom = Math.min(fits, PREVIEW_ZOOM_MAX, Math.max(1, zoom));
  };
  private wheelScrollsUnder(e: WheelEvent): boolean {
    return e.composedPath().some((t) => {
      if (!(t instanceof HTMLElement) || t === this) return false;
      const overflow = getComputedStyle(t).overflowY;
      return (overflow === "auto" || overflow === "scroll") && t.scrollHeight > t.clientHeight;
    });
  }
  private readonly onClick = (e: MouseEvent): void => {
    if (!this.swallowClick) return;
    this.swallowClick = false;
    e.stopPropagation();
    e.preventDefault();
  };
  private readonly onKeyDown = (e: KeyboardEvent): void => this.keyDown(e);
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (e.key === "Control" || e.key === "Meta") this.ctrlHeld = false;
    // Released over a card, Alt doesn't also open the browser's menu bar, as it would on Windows.
    if (e.key === "Alt") { this.altHeld = false; if (this.hoverOid !== null) e.preventDefault(); }
  };
  private readonly onBlur = (): void => { this.ctrlHeld = false; this.altHeld = false; };
  private readonly onResize = (): void => { this.requestUpdate(); };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    window.addEventListener("resize", this.onResize);
    this.addEventListener("pointerdown", this.onPointerDown, { capture: true });
    this.addEventListener("click", this.onClick, { capture: true });
    this.addEventListener("wheel", this.onWheel, { passive: false });
    this.sizer.observe(this);
  }

  // A phone's screen is too small for the table's layout, so it's laid out at its minimum size and
  // scaled down to fit, rather than squeezed until things overlap.
  private refit(): void {
    const w = this.clientWidth;
    const h = this.clientHeight;
    if (w === 0 || h === 0) return;
    const s = Math.min(1, w / TABLE_MIN_W, h / TABLE_MIN_H);
    const fit = { w: w / s, h: h / s, s };
    if (this.fit?.w !== fit.w || this.fit.h !== fit.h || this.fit.s !== fit.s) this.fit = fit;
  }

  disconnectedCallback(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    window.removeEventListener("resize", this.onResize);
    this.sizer.disconnect();
    this.removeEventListener("pointerdown", this.onPointerDown, { capture: true });
    this.removeEventListener("click", this.onClick, { capture: true });
    this.removeEventListener("wheel", this.onWheel);
    this.landFlights();
    this.lookSender?.stop();
    this.lookSender = null;
    if (this.settleTimer !== null) window.clearTimeout(this.settleTimer);
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    super.disconnectedCallback();
  }

  // Arrows are measured from where things were drawn, once now and again after cards finish moving.
  updated(): void {
    const strikes = this.strikes;
    const lifeDeltas = this.lifeDeltas;
    this.strikes = [];
    this.lifeDeltas = [];
    const impact = strikes.some((s) => s.combat) ? IMPACT_MS : 0;
    if (this.takeoffs.length > 0) {
      const flights = fly(this.renderRoot, this.takeoffs, impact > 0 ? impact + 180 : 0);
      this.takeoffs = [];
      this.flights = flights;
      if (flights.length > 0) void Promise.allSettled(flights.map((f) => f.finished)).then(() => { if (this.flights === flights) this.measure(); });
    }
    if (strikes.length > 0) playStrikes(this.renderRoot, strikes, this.view.moves);
    if (lifeDeltas.length > 0) pulseLife(this.renderRoot, lifeDeltas, impact);
    this.answerPlannedTrigger();
    this.sendLook();
    requestAnimationFrame(() => this.measure());
    if (this.settleTimer !== null) window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => { this.settleTimer = null; this.measure(); }, 240);
  }

  private landFlights(): void {
    land(this.flights);
    this.flights = [];
  }

  private measure(): void {
    const layer = this.renderRoot.querySelector(".table");
    if (!layer || !this.shadowRoot) return;
    const next = measureArrows(this.shadowRoot, layer, this.arrowSpecs(), this.cursor);
    if (!sameArrows(next, this.arrows)) this.arrows = next;
    const rings = this.measureLookRings(layer);
    if (!sameRings(rings, this.lookRings)) this.lookRings = rings;
    const sizes = [...this.renderRoot.querySelectorAll<HTMLElement>("[data-field]")].map((f) => `${f.clientWidth}x${f.clientHeight}`).join();
    if (sizes !== this.fieldSizes) { this.fieldSizes = sizes; this.requestUpdate(); }
  }

  private sendLook(): void {
    const intents = this.intents;
    if (!intents || this.inPregame() || !this.st.players[this.view.seat]) return;
    this.lookSender ??= new LookSender((look) => intents.lookAt(look));
    const shown = this.zoneShown;
    this.lookSender.point(lookOf(this.st, this.view.seat, {
      hoverOid: this.hoverOid,
      handOrder: this.handOrder,
      zone: this.pointedZone ?? (shown ? { pid: shown.pid, zone: shown.zone } : null),
      seat: this.pointedSeat,
    }));
  }

  private measureLookRings(layer: Element): LookRing[] {
    const origin = layer.getBoundingClientRect();
    const k = tableScale(this.renderRoot);
    const lookers = Object.keys(this.view.looks).filter((seat) => seat !== this.view.seat && this.st.players[seat]);
    const named = lookers.length > 1 || this.multi() || !this.intents;
    const out: LookRing[] = [];
    for (const seat of lookers) {
      for (const anchor of lookAnchors(this.st, this.view.looks[seat]!)) {
        const el = this.renderRoot.querySelector(`[data-anchor~="${CSS.escape(anchor)}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.width === 0) continue;
        out.push({ key: seat, x: (r.left - origin.left) / k - 5, y: (r.top - origin.top) / k - 5, w: r.width / k + 10, h: r.height / k + 10, name: named ? playerName(this.st, seat) : null });
        break;
      }
    }
    return out;
  }

  // Each stack object to its targets, the hovered one's standing out; and while aiming, the source to
  // what's picked and on to the pointer.
  private arrowSpecs(): ArrowSpec[] {
    const out: ArrowSpec[] = [];
    for (const item of this.st.stack) {
      const id = stackItemObjectId(item);
      const tone = this.hoverStack === null || this.hoverStack === id ? "target" : "faint";
      for (const t of stackItemTargets(item)) out.push({ from: `stack:${id}`, to: targetAnchor(t), tone });
    }
    if (this.aim) {
      const from = this.renderRoot.querySelector('[data-anchor~="aim-source"]') ? "aim-source" : `obj:${this.aim.source}`;
      for (const t of this.aim.picked) out.push({ from, to: targetAnchor(t), tone: "aim" });
      out.push({ from, to: "cursor", tone: "aim" });
    }
    return [...out, ...this.combatArrows()];
  }

  // Attackers to what they attack and blockers to what they block, as declared or as being declared.
  // A blocked attack shows only its blocks.
  private combatArrows(): ArrowSpec[] {
    const st = this.st;
    const d = this.decision;
    const out: ArrowSpec[] = [];
    const attacks = d.kind === "ViewerDeclaresAttackers" ? this.attackPlan : st.attackers;
    const blocks = d.kind === "ViewerDeclaresBlockers" ? this.blockPlan : st.blocks;
    for (const a of attacks) {
      if (blocks.some((b) => b.attacker === a.attacker)) continue;
      const walker = optionValue(a.planeswalker);
      const mine = controllerOf(st, a.attacker) === this.view.seat || a.defender === this.view.seat;
      out.push({ from: `obj:${a.attacker}`, to: walker === null ? `seat:${a.defender}` : `obj:${walker}`, tone: mine ? "attack" : "faint" });
    }
    for (const b of blocks) out.push({ from: `obj:${b.blocker}`, to: `obj:${b.attacker}`, tone: "block" });
    if (this.dragging?.kind === "attack" || this.dragging?.kind === "block") out.push({ from: `obj:${this.dragging.oid}`, to: "cursor", tone: this.dragging.kind });
    else if (this.pickedBlocker !== null) out.push({ from: `obj:${this.pickedBlocker}`, to: "cursor", tone: "block" });
    return out;
  }

  // A new decision starts its answer afresh: picks, menus and payment taps belong to the last one. An
  // aim lasts while everything it could still send is legal.
  willUpdate(changed: PropertyValues<this>): void {
    const before = changed.get("view") as BoardView | undefined;
    if (changed.has("view")) {
      this.announceTurn();
      this.syncHandOrder();
    }
    if (before && before.moves !== this.view.moves) {
      this.landFlights();
      this.takeoffs = measureTakeoffs(this.renderRoot, this.view.moves);
      this.arrived = arrivals(before.state, this.view.state, this.view.seat);
    }
    if (before && before.history !== this.view.history) this.measureHits(before);
    if (!changed.has("view") || before?.state === this.view.state) return;
    this.cardMenu = null;
    this.whyShown = null;
    if (!this.choiceHidden()) this.hiddenChoice = null;
    const legal = new Set(this.legal().map((a) => JSON.stringify(a)));
    if (this.aim && !this.aim.options.every((a) => legal.has(JSON.stringify(a)))) this.cancelAim();
    if (before?.state.decision.kind !== this.decision.kind) {
      this.attackPlan = [];
      this.blockPlan = [];
      this.pickedBlocker = null;
      this.attackTo = null;
    }
    if (this.decision.kind !== "ViewerPutsOnBottom") this.bottomPicks = new Set();
    const spec = this.choiceSpec();
    this.payPicks = spec?.kind === "ChoosePayment" ? [...spec.values[0].suggested] : [];
    this.tapPicks = spec?.kind === "ChooseToTap" ? [...spec.values[0].suggested] : [];
    this.damageOrder = spec?.kind === "ChooseDamageAssignment" ? spec.values[0].blockers.map((b) => b.blocker) : [];
    this.viewingBattlefield = false;
    if (isNameQuestion(spec)) void this.nameLists.fetch();
    else this.namePick = null;
    const asker = this.askingCard();
    const askerFace = asker ? knownFace(asker) : null;
    if (askerFace) void this.oracle.fetch(askerFace.name);
    if (spec?.kind === "ChooseTriggerOrder") for (const t of spec.values[0]) void this.oracle.fetch(knownFace(t.source)?.name ?? "");
    for (const item of this.st.stack) if (item.kind === "AbilityItem") void this.oracle.fetch(knownFace(item.values[0].source)?.name ?? "");
    this.dropHoverOfGone();
  }

  // What leaves while hovered gets no mouseleave: a stack object that left the stack, or a card
  // that's nowhere now, stops being the hovered one.
  private dropHoverOfGone(): void {
    const hovered = this.hoverStack;
    if (hovered !== null && !this.st.stack.some((item) => stackItemObjectId(item) === hovered)) {
      this.hoverStack = null;
      if (this.hoverOid === hovered) this.hoverOid = null;
    }
    if (this.hoverOid !== null && !this.cardFor(this.hoverOid)) this.hoverOid = null;
  }

  // ------------------------------------------------------------------ what the view says

  private get st(): PublicState { return this.view.state; }

  private get decision(): PublicDecision { return this.view.state.decision; }

  // The viewer's seat, or for a viewer without one the first in turn order, who sits at the bottom.
  private bottomSeat(): string {
    return this.st.players[this.view.seat] ? this.view.seat : this.st.turn_order[0] ?? this.view.seat;
  }

  private seated(): boolean { return this.st.players[this.view.seat] !== undefined; }

  private get acting(): boolean { return this.intents !== null && this.seated(); }

  private legal(): Offer[] {
    const d = this.decision;
    switch (d.kind) {
      case "ViewerActs": return d.values[0];
      case "ViewerDeclaresAttackers":
      case "ViewerDeclaresBlockers": return d.values[1];
      case "AnotherPlayerDecides": return d.values[2];
      default: return [];
    }
  }

  private choiceSpec(): PublicChoiceSpec | null {
    return this.decision.kind === "ViewerChooses" ? this.decision.values[0] : null;
  }

  // The card whose effect asks the question, shown beside it so the viewer can read what the
  // choice is for; null for a question nothing asks, such as a trigger order.
  private askingCard(): PublicCard | null {
    return this.decision.kind === "ViewerChooses" ? optionValue(this.decision.values[1]) : null;
  }

  private choiceHidden(): boolean {
    const spec = this.choiceSpec();
    return spec !== null && this.hiddenChoice === JSON.stringify(spec);
  }

  private hideChoice(): void {
    this.hiddenChoice = JSON.stringify(this.choiceSpec());
  }

  private declaring(): Declaring | null {
    const d = this.decision;
    if (d.kind === "ViewerDeclaresAttackers") return { kind: "attackers", groups: d.values[0] };
    if (d.kind === "ViewerDeclaresBlockers") return { kind: "blockers", options: d.values[0] };
    return null;
  }

  // Whom the game waits on.
  private decider(): string | null {
    const d = this.decision;
    switch (d.kind) {
      case "AnotherPlayerDecides": return d.values[0];
      case "GameEnded": return null;
      default: return this.view.seat;
    }
  }

  private actionsOn(oid: number): Offer[] {
    return this.legal().filter((o) => actionPrimaryOid(offerAction(o)) === oid);
  }

  // The hand's order belongs to this view, not to the game state: it only changes what this viewer
  // sees and follows a card as it leaves or enters the hand.
  private syncHandOrder(): void {
    const cards = cardsIn(this.st.players[this.view.seat]?.hand ?? {}).map((c) => c.object_id);
    if (this.handOrderGame !== this.view.game) {
      this.handOrderGame = this.view.game;
      this.handOrder = cards;
      return;
    }
    const present = new Set(cards);
    this.handOrder = [...this.handOrder.filter((oid) => present.has(oid)), ...cards.filter((oid) => !this.handOrder.includes(oid))];
  }

  // Why the engine refuses each way the viewer might use this card of theirs now (#202).
  private refusalsOn(oid: number): PublicRefusal[] {
    return this.acting ? this.st.refusals.filter((r) => actionPrimaryOid(r.action) === oid) : [];
  }

  // A card with no way to use it now, and the engine's reasons why.
  private unusable(oid: number): PublicRefusal[] {
    return this.actionsOn(oid).length === 0 ? this.refusalsOn(oid) : [];
  }

  // The sources auto-pay would tap for the hovered card (601.2g), lit on the battlefield.
  private plannedTaps(): Set<number> {
    if (this.hoverOid === null || this.decision.kind !== "ViewerActs") return new Set();
    const planned = this.st.planned_taps.find((p) => actionPrimaryOid(p.action) === this.hoverOid);
    return new Set(planned ? planned.taps.map((t) => t.object_id) : []);
  }

  // ------------------------------------------------------------------ sending

  private take(action: Action): void {
    if (!this.intents) return;
    const hold = (this.ctrlHeld || this.holdNext) && action.kind !== "PassPriority" && action.kind !== "Concede";
    this.intents.takeAction(action, hold);
    this.holdNext = false;
    this.cardMenu = null;
    this.cancelAim();
  }

  private cancelAim(): void {
    this.aim = null;
    this.cursor = null;
    this.pressed = null;
  }

  // What the board is asking the viewer to point at: the next target while aiming, or a target
  // question's candidates. Null when it isn't asking.
  private targetCandidates(): Target[] | null {
    if (this.aim) return aimCandidates(this.aim);
    const spec = this.choiceSpec();
    return spec?.kind === "ChooseTarget" ? spec.values[1] : null;
  }

  // A card question's candidates (#191): each is clicked on the board, or in the list for those not on it.
  private cardCandidates(): number[] | null {
    const spec = this.choiceSpec();
    return spec?.kind === "ChooseCard" ? spec.values[1] : null;
  }

  private isTargetable(t: Target): boolean {
    const candidates = this.targetCandidates();
    return candidates !== null && isCandidate(candidates, t);
  }

  // A click on something the board is asking for answers with it; true when it did.
  private pick(t: Target): boolean {
    if (!this.acting || !this.isTargetable(t)) return false;
    if (!this.aim) {
      this.answer({ kind: "ChoseTarget", values: [t] });
      return true;
    }
    const next = aimedAt(this.aim, t);
    const done = aimComplete(next);
    if (done && aimCandidates(next).length === 0) this.take(done);
    else this.aim = next;
    return true;
  }

  // While aiming, a click on a target already picked puts it back; true when it did.
  private unpick(t: Target): boolean {
    if (!this.acting || !this.aim || !isPicked(this.aim, t)) return false;
    const without = aimedWithout(this.aim, t);
    if (without) this.aim = without;
    return without !== null;
  }

  private pickCard(oid: number): boolean {
    const candidates = this.cardCandidates();
    if (!this.acting || !candidates?.includes(oid)) return false;
    this.answer({ kind: "ChoseCard", values: [oid] });
    return true;
  }

  // A click on a permanent group, a hand card or a stack object: an answer if it's asked for, a
  // creature in a declaration, else the first of its cards with something to do.
  private clickObjects(oids: number[]): void {
    if (this.swallowClickAfterDrag()) return;
    for (const oid of oids) if (this.pick(objectTarget(oid)) || this.pickCard(oid)) return;
    // A pile's last picked card is put back first.
    for (const oid of [...oids].reverse()) if (this.unpick(objectTarget(oid))) return;
    if (this.aim || this.targetCandidates() || this.cardCandidates()) return;
    if (this.clickInDeclaration(oids) || this.clickInPayment(oids)) return;
    const usable = oids.find((oid) => this.actionsOn(oid).length > 0);
    if (usable !== undefined) { this.useCard(usable); return; }
    const refused = oids.find((oid) => this.unusable(oid).length > 0);
    this.whyShown = refused === undefined || refused === this.whyShown ? null : refused;
  }

  // While attacking, a click on an opponent sends the next attackers at them.
  private clickSeat(pid: string): void {
    if (this.swallowClickAfterDrag() || this.pick(playerTarget(pid)) || this.unpick(playerTarget(pid))) return;
    const d = this.decision;
    if (d.kind === "ViewerDeclaresAttackers" && isAttackablePlayer(d.values[0], pid)) this.attackTo = { pid, planeswalker: null };
  }

  // The click a drag ends with isn't a click.
  private swallowClickAfterDrag(): boolean {
    const ended = this.dragEnded;
    this.dragEnded = false;
    return ended;
  }

  // Attackers are clicked in and out of the attack; a blocker is clicked, then the attacker it blocks.
  private clickInDeclaration(oids: number[]): boolean {
    if (!this.acting) return false;
    const d = this.decision;
    if (d.kind === "ViewerDeclaresAttackers") {
      const groups = d.values[0];
      const walker = oids.find((oid) => isAttackablePlaneswalker(groups, oid));
      if (walker !== undefined) { this.attackTo = { pid: controllerOf(this.st, walker) ?? "", planeswalker: walker }; return true; }
      const attacking = oids.find((oid) => this.attackPlan.some((a) => a.attacker === oid));
      if (attacking !== undefined) { this.attackPlan = withoutAttack(this.attackPlan, attacking); return true; }
      const ready = oids.find((oid) => canAttack(groups, oid));
      if (ready === undefined) return false;
      this.attackPlan = withAttack(this.attackPlan, groups, ready, this.defaultDefender());
      return true;
    }
    if (d.kind === "ViewerDeclaresBlockers") {
      const options = d.values[0];
      const picked = this.pickedBlocker;
      const blocked = picked === null ? undefined : oids.find((oid) => canBlock(options, picked, oid));
      if (picked !== null && blocked !== undefined) {
        this.blockPlan = withBlock(this.blockPlan, picked, blocked);
        this.pickedBlocker = null;
        return true;
      }
      const blocking = oids.find((oid) => this.blockPlan.some((b) => b.blocker === oid));
      if (blocking !== undefined) { this.blockPlan = withoutBlock(this.blockPlan, blocking); return true; }
      const blocker = oids.find((oid) => isBlocker(options, oid));
      if (blocker === undefined) return false;
      this.pickedBlocker = this.pickedBlocker === blocker ? null : blocker;
      return true;
    }
    return false;
  }

  private defaultDefender(): Defender | null {
    const d = this.decision;
    if (d.kind !== "ViewerDeclaresAttackers") return null;
    return this.attackTo ?? firstDefender(this.st, this.view.seat, d.values[0]);
  }

  // What the blocks answer to besides who may block what (509.1b-c, 702.111b).
  private blockAskText(options: BlockOptions): string[] {
    const st = this.st;
    const asks = blockAsks(options);
    return [
      ...countsOf(asks.needMore.map((a) => a.least)).map((n) => `${countWord(n, true)} or more must block ${listed(st, asks.needMore.filter((a) => a.least === n).map((a) => a.attacker))}, or none.`),
      ...countsOf(asks.atMost.map((a) => a.most)).map((n) => `No more than ${countWord(n, false)} may block ${listed(st, asks.atMost.filter((a) => a.most === n).map((a) => a.attacker))}.`),
      ...(asks.lures.length === 0 ? [] : [`Every creature able to block ${listed(st, asks.lures)} must block it.`]),
      ...(asks.mustBeBlocked.length === 0 ? [] : [`Must be blocked if able: ${listed(st, asks.mustBeBlocked)}.`]),
      ...(asks.mustBlock.length === 0 ? [] : [`Must block if able: ${listed(st, asks.mustBlock)}.`]),
      ...(asks.notAlone.length === 0 ? [] : [`Can't block alone: ${listed(st, asks.notAlone)}.`]),
    ];
  }

  private attackAll(): void {
    const d = this.decision;
    if (d.kind === "ViewerDeclaresAttackers") this.attackPlan = attackingWithAll(this.attackPlan, d.values[0], this.defaultDefender());
  }

  private declareAttacks(attacks: Attack[]): void {
    this.intents?.declareAttackers(attacks);
  }

  private declareBlocks(blocks: Block[]): void {
    this.pickedBlocker = null;
    this.intents?.declareBlockers(blocks);
  }

  private moveDamageBlocker(oid: number, by: number): void {
    const at = this.damageOrder.indexOf(oid) + by;
    if (at >= 0 && at < this.damageOrder.length) this.damageOrder = movedInOrder(this.damageOrder, oid, at);
  }

  private dragDamageBlocker(e: PointerEvent, oid: number): void {
    const others = [...(this.shadowRoot?.querySelectorAll<HTMLElement>(".damage-to") ?? [])].filter((el) => Number(el.dataset["blocker"]) !== oid);
    const at = others.filter((el) => { const r = el.getBoundingClientRect(); return r.left + r.width / 2 < e.clientX; }).length;
    if (this.damageOrder[at] !== oid) this.damageOrder = movedInOrder(this.damageOrder, oid, at);
  }

  private dragArrangedCard(e: PointerEvent, card: number): void {
    const spec = this.choiceSpec();
    if (spec?.kind !== "ChooseArrangement" || !this.shadowRoot) return;
    const a = spec.values[0];
    const drop = arrangementDrop(this.shadowRoot, a, card, e.clientX, e.clientY);
    const piles = this.arrangedPiles(a);
    if (!drop || piles[drop.pile]?.indexOf(card) === drop.at) return;
    this.arranged = { question: a, piles: placedInPile(piles, card, drop.pile, drop.at) };
  }

  // One way to use the card is taken, or aimed when it takes targets; more than one asks which, and
  // a double-faced card played asks which face, however many ways there are.
  private useCard(oid: number): void {
    if (!this.acting || this.choiceSpec() || this.aim?.source === oid) return;
    const ways = waysToUse(this.actionsOn(oid));
    if (ways.length === 0) return;
    if (!this.asksWay(oid, ways)) { this.useWay(oid, ways[0]!); return; }
    const faces = this.facesToPick(oid, ways);
    void this.oracle.fetch(faces ? faces.front : lookupCardNameAnywhere(this.st, oid));
    this.cardMenu = { oid, ways };
  }

  // Whether using the card asks how first, rather than taking its one way.
  private asksWay(oid: number, ways: Offer[][]): boolean {
    if (ways.length !== 1) return true;
    return this.facesToPick(oid, ways) !== null || this.offersLoyaltyAbility(oid, ways) || this.onlyAlternativeCast(oid, ways);
  }

  // A card whose one way is an alternative cost, as warp or evoke, while its normal cast is refused
  // (not enough mana, say), still asks: a click mustn't pick that cost and what it does unseen. A
  // split card has no normal cast, so the half its mana pays for is still cast at a click.
  private onlyAlternativeCast(oid: number, ways: Offer[][]): boolean {
    return ways.length === 1 && offerAction(ways[0]![0]!).kind === "CastAlternative"
      && this.castsRefusedBeside(oid, ways).some((r) => r.action.kind === "CastSpell");
  }

  // The card's casts the engine refuses now, besides the ways it offers.
  private castsRefusedBeside(oid: number, ways: Offer[][]): PublicRefusal[] {
    const offered = new Set(ways.map((way) => wayOf(offerAction(way[0]!))));
    const casts = ["CastSpell", "CastAlternative", "CastBackFace"];
    return this.refusalsOn(oid).filter((r) => casts.includes(r.action.kind) && !offered.has(wayOf(r.action)));
  }

  // A double-faced card's faces, when a way of using it plays one of them.
  private facesToPick(oid: number, ways: Offer[][]): PublicFaces | null {
    const c = this.cardFor(oid);
    const faces = c ? facesOf(c) : null;
    return faces && ways.some(playsCard) ? faces : null;
  }

  private useWay(oid: number, way: Offer[]): void {
    this.cardMenu = null;
    if (!takesTargets(way)) { this.take(offerAction(way[0]!)); return; }
    const aim = aimAt(oid, way, []);
    const done = aimComplete(aim);
    if (done && aimCandidates(aim).length === 0) this.take(done);
    else this.aim = aim;
  }

  // A press may become a drag: reorder a hand card, play it onto the battlefield, aim it at a target,
  // or move a creature onto the player, planeswalker or attacker it relates to in combat.
  private pressCard(e: PointerEvent, oid: number, fromHand = false): void {
    if (!this.acting || this.aim || e.button !== 0) return;
    const d = this.decision;
    const at = { oid, x: e.clientX, y: e.clientY };
    if (fromHand) { if (!this.choiceSpec()) this.pressed = { ...at, kind: "hand" }; return; }
    if (d.kind === "ViewerDeclaresAttackers") { if (canAttack(d.values[0], oid)) this.pressed = { ...at, kind: "attack" }; return; }
    if (d.kind === "ViewerDeclaresBlockers") { if (isBlocker(d.values[0], oid)) this.pressed = { ...at, kind: "block" }; return; }
    if (this.choiceSpec()) return;
    const ways = waysToUse(this.actionsOn(oid));
    if (ways.length === 1 && takesTargets(ways[0]!) && !this.asksWay(oid, ways)) this.pressed = { ...at, kind: "aim" };
  }

  private onPointerMove(e: PointerEvent): void {
    const p = this.pressed;
    if (p && !this.dragging && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 8) {
      this.dragging = { oid: p.oid, kind: p.kind };
      playSound("card-pickup");
      if (p.kind === "hand") {
        const card = this.shadowRoot?.querySelector<HTMLElement>(`.hand-card[data-oid="${p.oid}"]`);
        this.handDragOrigin = {
          index: this.handOrder.indexOf(p.oid),
          rotation: Number.parseFloat(card?.style.getPropertyValue("--rot") ?? "0") || 0,
          offsetY: Number.parseFloat(card?.style.getPropertyValue("--ty") ?? "0") || 0,
        };
      }
      if (p.kind === "aim") this.useCard(p.oid);
    }
    if (this.dragging?.kind === "hand") {
      const target = this.handDropPosition(e, this.dragging.oid);
      if (target) this.moveHandCard(this.dragging.oid, target.oid, target.before);
    }
    if (this.dragging?.kind === "damage") this.dragDamageBlocker(e, this.dragging.oid);
    if (this.dragging?.kind === "arrange") this.dragArrangedCard(e, this.dragging.oid);
    if (this.aim || this.dragging || this.pickedBlocker !== null) this.cursor = { x: e.clientX, y: e.clientY };
  }

  private onPointerUp(e: PointerEvent): void {
    const dragged = this.dragging;
    const handTarget = dragged?.kind === "hand" ? this.handDropPosition(e, dragged.oid) : null;
    this.pressed = null;
    this.dragging = null;
    this.handDragOrigin = null;
    if (!dragged) return;
    playSound("card-drop");
    this.dragEnded = true;
    window.setTimeout(() => { this.dragEnded = false; }, 0);
    if (this.pickedBlocker === null && !this.aim) this.cursor = null;
    const anchors = this.anchorsUnder(e, dragged.kind === "hand" ? dragged.oid : undefined);
    const seat = anchors.find((a) => a.startsWith("seat:"))?.slice(5);
    const objects = anchors.filter((a) => a.startsWith("obj:")).map((a) => Number(a.slice(4)));
    const d = this.decision;
    if (dragged.kind === "hand") {
      if (handTarget) this.moveHandCard(dragged.oid, handTarget.oid, handTarget.before);
      else {
        const onBattlefield = this.onOwnBattlefield(e);
        const actions = this.actionsOn(dragged.oid);
        const ways = waysToUse(actions);
        const canAim = ways.length === 1 && takesTargets(ways[0]!) && !this.facesToPick(dragged.oid, ways) && !this.onlyAlternativeCast(dragged.oid, ways);
        if (actions.length > 0 && (onBattlefield || (canAim && (seat !== undefined || objects.length > 0)))) {
          this.useCard(dragged.oid);
          if (canAim) {
            if (seat !== undefined) this.pick(playerTarget(seat));
            else objects.some((oid) => this.pick(objectTarget(oid)));
          }
        } else if (onBattlefield && this.unusable(dragged.oid).length > 0) this.whyShown = dragged.oid;
      }
    } else if (dragged.kind === "aim") {
      if (seat !== undefined) this.pick(playerTarget(seat));
      else objects.some((oid) => this.pick(objectTarget(oid)));
    } else if (dragged.kind === "attack" && d.kind === "ViewerDeclaresAttackers") {
      const groups = d.values[0];
      const walker = objects.find((oid) => canAttackAt(groups, dragged.oid, { pid: controllerOf(this.st, oid) ?? "", planeswalker: oid }));
      const to: Defender | null = walker !== undefined ? { pid: controllerOf(this.st, walker) ?? "", planeswalker: walker }
        : seat !== undefined && canAttackAt(groups, dragged.oid, { pid: seat, planeswalker: null }) ? { pid: seat, planeswalker: null } : null;
      if (to) this.attackPlan = withAttack(this.attackPlan, groups, dragged.oid, to);
    } else if (dragged.kind === "block" && d.kind === "ViewerDeclaresBlockers") {
      const attacker = objects.find((oid) => canBlock(d.values[0], dragged.oid, oid));
      if (attacker !== undefined) {
        this.blockPlan = withBlock(this.blockPlan, dragged.oid, attacker);
        this.pickedBlocker = null;
      }
    }
  }

  private handDropPosition(e: PointerEvent, except: number): { oid: number; before: boolean } | null {
    const hand = this.shadowRoot?.querySelector<HTMLElement>(".hand");
    const zone = this.shadowRoot?.querySelector<HTMLElement>(".handzone");
    const rect = zone?.getBoundingClientRect();
    if (!hand || !rect || e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return null;
    const cards = [...hand.querySelectorAll<HTMLElement>(".hand-card")].filter((card) => Number(card.dataset["oid"]) !== except);
    const handLeft = hand.getBoundingClientRect().left;
    const k = tableScale(this.renderRoot);
    const next = cards.find((card) => e.clientX < handLeft + (card.offsetLeft + card.offsetWidth / 2) * k);
    const target = next ?? cards.at(-1);
    if (!target) return null;
    return { oid: Number(target.dataset["oid"]), before: next !== undefined };
  }

  private onOwnBattlefield(e: PointerEvent): boolean {
    return (this.shadowRoot?.elementsFromPoint(e.clientX, e.clientY) ?? [])
      .some((el) => (el as HTMLElement).closest(".mine") !== null);
  }

  private moveHandCard(oid: number, target: number, before: boolean): void {
    if (this.view.seat !== this.bottomSeat() || oid === target) return;
    const order = [...this.handOrder];
    const from = order.indexOf(oid);
    const to = order.indexOf(target);
    if (from < 0 || to < 0) return;
    order.splice(from, 1);
    const targetAt = order.indexOf(target);
    order.splice(targetAt + (before ? 0 : 1), 0, oid);
    if (order.every((card, i) => card === this.handOrder[i])) return;
    this.handOrder = order;
    this.requestUpdate();
  }

  private anchorsUnder(e: PointerEvent, exceptHandCard?: number): string[] {
    const el = this.shadowRoot?.elementsFromPoint(e.clientX, e.clientY).find((x) => {
      const node = x as HTMLElement;
      return !!node.dataset?.["anchor"]
        && !(exceptHandCard !== undefined && node.classList.contains("hand-card") && Number(node.dataset["oid"]) === exceptHandCard);
    });
    return ((el as HTMLElement | undefined)?.dataset["anchor"] ?? "").split(" ").filter(Boolean);
  }

  private passPriority(): void {
    const pass = this.legal().map(offerAction).find((a) => a.kind === "PassPriority");
    if (pass && this.decision.kind === "ViewerActs") this.take(pass);
  }

  private answer(answer: ChoiceResult): void {
    this.hiddenChoice = null;
    this.intents?.answerChoice(answer);
  }

  private setStops(stops: Stops): void {
    this.intents?.setStops(stops);
  }

  private autoPassCards(): string[] {
    return this.view.pacing?.auto_pass_cards ?? [];
  }

  private toggleAutoPass(name: string): void {
    const cards = this.autoPassCards();
    this.intents?.setAutoPassCards(cards.includes(name) ? cards.filter((n) => n !== name) : [...cards, name]);
    this.stackMenu = null;
  }

  private autoAnswers(): AutoAnswer[] {
    return this.view.pacing?.auto_answers ?? [];
  }

  // How the Room answers this card's optional questions from now on, or null for asking again. A
  // question of the card's that's waiting is answered by the Room at once.
  private setAutoAnswer(card: string, answer: AlwaysAnswer | null): void {
    const others = this.autoAnswers().filter((a) => a.card !== card);
    this.intents?.setAutoAnswers(answer === null ? others : [...others, { card, answer }]);
  }

  private resolveAll(): void {
    if (this.st.stack.length > 0) this.intents?.resolveStack();
  }

  private undoableActions(): number {
    return this.view.undoable?.actions ?? 0;
  }

  // An aim the Room hasn't been sent is put back first.
  private undoLastAction(): void {
    if (!this.acting) return;
    if (this.aim) { this.cancelAim(); return; }
    if (this.undoableActions() === 0) return;
    this.cardMenu = null;
    this.intents?.undoLastAction();
  }

  // The popup over the board: one the viewer opened (a card's ways or faces, a public zone), or a
  // question the game asks there rather than on the board itself.
  private popOpen(): "viewer" | "question" | null {
    if (this.zoneShown || (this.cardMenu && !this.choiceSpec())) return "viewer";
    const spec = this.choiceSpec();
    if (!this.acting || !spec || spec.kind === "ChooseTarget" || (spec.kind === "ChooseCard" && !this.overlaidCardChoice(spec)) || this.choiceHidden()) return null;
    return this.view.autoPaying ? null : "question";
  }

  // A press outside the popup: not on it, nor on the action buttons that answer it, the log, an
  // error, or while paying, a source or creature it can tap on the battlefield (601.2g-h, 702.122a).
  private pressedOutsidePop(e: Event): boolean {
    if (!this.popOpen() || (this.peekingAt !== null && this.peekingAt === this.choiceSpec())) return false;
    const kind = this.choiceSpec()?.kind;
    const paying = kind === "ChoosePayment" || kind === "ChooseToTap";
    const inside = `.pop, .actions, .drawer, .drawer-tab, .error${paying ? ", .perm.payable, .perm.will-tap" : ""}`;
    const path = e.composedPath();
    return !path.slice(0, path.indexOf(this)).some((t) => t instanceof Element && t.matches(inside));
  }

  // A press outside the popup and Esc cancel it, as its Cancel does: one the viewer opened closes, and
  // a question takes back the action that asked it. True when it cancelled.
  private cancelPop(): boolean {
    if (this.popOpen() !== "viewer") return this.cancelQuestion();
    this.cardMenu = null;
    this.zoneShown = null;
    return true;
  }

  // Through the Room's undo (#200), when the Room allows.
  private cancelQuestion(): boolean {
    if (this.popOpen() !== "question" || this.undoableActions() === 0) return false;
    if (this.cancelSentIn !== this.st) {
      this.cancelSentIn = this.st;
      this.intents?.undoLastAction();
    }
    return true;
  }

  // Space passes and Enter ends the turn, as on Arena; Ctrl or Cmd+Z undoes; L opens the log; never while typing.
  private keyDown(e: KeyboardEvent): void {
    this.landFlights();
    if (e.key === "Control" || e.key === "Meta") { this.ctrlHeld = true; return; }
    if (e.key === "Alt") { this.altHeld = true; return; }
    // Esc puts back what the viewer has open here, or else cancels the popup's question, even from its box.
    if (e.key === "Escape" && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const open = this.cardMenu || this.zoneShown || this.stackMenu !== null || this.moreOpen || this.logMenu || this.pickedBlocker !== null || this.aim || this.whyShown !== null || this.loopPromptOpen;
      this.cardMenu = null; this.zoneShown = null; this.stackMenu = null; this.pickedBlocker = null; this.whyShown = null; this.loopPromptOpen = false; this.moreOpen = false; this.logMenu = null; this.cancelAim();
      if (!open) this.cancelPop();
      return;
    }
    const typing = e.composedPath().some((t) => t instanceof HTMLInputElement || t instanceof HTMLSelectElement || t instanceof HTMLTextAreaElement);
    if (!typing && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "z") {
      e.preventDefault();
      this.undoLastAction();
      return;
    }
    if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === "l" || e.key === "L") { this.logOpen = !this.logOpen; return; }
    if (!this.acting || this.cardMenu || this.aim || this.inPregame()) return;
    // In a declaration, a damage assignment, a payment or an arrangement, Space answers as the big button does.
    const confirmed = ["ChooseDamageAssignment", "ChoosePayment", "ChooseToTap", "ChooseArrangement"];
    if (this.declaring() || confirmed.includes(this.choiceSpec()?.kind ?? "")) {
      if (e.key === " ") { e.preventDefault(); this.paceButtons().primary?.run?.(); }
      return;
    }
    // A name or a number is confirmed with Enter as the big button says, even once its box has lost
    // focus, as it does while the board is inert to reconnect; a listed choice is picked by its number.
    const spec = this.choiceSpec();
    if (spec) {
      if (e.key === "Enter" && (isNameQuestion(spec) || spec.kind === "ChooseInt")) { e.preventDefault(); this.paceButtons().primary?.run?.(); }
      const listed = spec.kind === "ChooseMode" ? spec.values[0].length : spec.kind === "ChooseOptional" ? 2 : 0;
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= Math.min(listed, 9)) { e.preventDefault(); this.answer({ kind: "ChoseMode", values: [n - 1] }); }
      return;
    }
    if (e.key === " " && e.shiftKey) { e.preventDefault(); this.resolveAll(); }
    else if (e.key === " ") { e.preventDefault(); this.passPriority(); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey && this.view.pacing?.stops.skip_turn_shown) this.intents?.skipTurn(this.st.turn);
      else this.intents?.endTurn(this.st.turn);
    }
  }

  private hover(oid: number | null, e?: MouseEvent): void {
    if (oid !== null && oid !== this.hoverOid && this.hoverLogged?.object_id !== oid && !this.dragging) playSound("card-hover");
    if (oid !== this.hoverOid) this.previewZoom = 1;
    this.hoverOid = oid;
    if (e) this.altHeld = e.altKey;
    if (e && this.view.previewAt === "auto") this.previewSide = e.clientX > window.innerWidth / 2 ? "at-left" : "at-right";
    const c = oid === null ? null : this.cardFor(oid);
    const face = c ? knownFace(c) : null;
    if (face) void this.oracle.fetch(face.name);
    const faces = c ? facesOf(c) : null;
    if (faces) void this.oracle.fetch(faces.front);
  }

  private cardFor(oid: number): PublicCard | null {
    return findCardAnywhere(this.st, oid) ?? (this.hoverLogged?.object_id === oid ? this.hoverLogged : null);
  }

  private isPlaneswalker(card: PublicCard | null): card is PublicCard {
    return card?.characteristics.types.some((t) => t.kind === "Planeswalker") ?? false;
  }

  // A planeswalker's abilities always open its menu, so the ones it can't use now show too.
  private offersLoyaltyAbility(oid: number, ways: Offer[][]): boolean {
    return this.isPlaneswalker(this.cardFor(oid)) && ways.some((way) => offerAction(way[0]!).kind === "ActivateAbility");
  }

  private hoverLoggedCard(c: PublicCard | null, e?: MouseEvent): void {
    this.hoverLogged = c;
    this.hover(c?.object_id ?? null, e);
  }

  // A card the viewer saw shows on hover, a click copies its name, and a right-click offers Scryfall;
  // a face-down one they didn't is only words.
  private logWords(line: LogLine): TemplateResult {
    return html`${line.parts.map((p) => typeof p === "string" ? p : !knownFace(p) ? cardLabel(p)
      : html`<span class="log-card ${this.copiedLogCard === p ? "copied" : ""}" title="Click to copy its name, right-click for Scryfall"
          @mouseenter=${(ev: MouseEvent) => this.hoverLoggedCard(p, ev)}
          @mouseleave=${() => { if (this.hoverLogged === p) this.hoverLoggedCard(null); }}
          @click=${(ev: MouseEvent) => { ev.stopPropagation(); this.copyLoggedName(p); }}
          @contextmenu=${(ev: MouseEvent) => { ev.preventDefault(); this.logMenu = { card: p, ...this.tablePoint(ev) }; }}>${cardLabel(p)}</span>`)}`;
  }

  private copyLoggedName(c: PublicCard): void {
    void navigator.clipboard?.writeText(loggedName(c)).catch(() => undefined);
    this.copiedLogCard = c;
    setTimeout(() => { if (this.copiedLogCard === c) this.copiedLogCard = null; }, 1200);
  }

  // Where the pointer is in the table's own pixels, kept far enough from the right edge for a menu.
  private tablePoint(e: MouseEvent): { x: number; y: number } {
    const table = this.renderRoot.querySelector<HTMLElement>(".table");
    if (!table) return { x: e.clientX, y: e.clientY };
    const r = table.getBoundingClientRect();
    const k = tableScale(this.renderRoot);
    return { x: Math.min((e.clientX - r.left) / k, table.offsetWidth - 200), y: (e.clientY - r.top) / k };
  }

  private renderLogMenu(): TemplateResult | typeof nothing {
    const m = this.logMenu;
    if (!m) return nothing;
    return html`<div class="menu log-menu" role="menu" style="left: ${m.x}px; top: ${m.y}px">
      <button role="menuitem" @click=${() => { this.copyLoggedName(m.card); this.logMenu = null; }}>Copy name</button>
      <a role="menuitem" href=${scryfallSearch(loggedName(m.card))} target="_blank" rel="noopener noreferrer"
        @click=${() => { this.logMenu = null; }}>Open on Scryfall</a>
    </div>`;
  }

  // ------------------------------------------------------------------ the table

  // Everyone but the bottom seat, left to right in turn order from the next player.
  private opponents(): string[] {
    const order = this.st.turn_order;
    const bottom = this.bottomSeat();
    const at = order.indexOf(bottom);
    return [...order.slice(at + 1), ...order.slice(0, Math.max(0, at))].filter((pid) => pid !== bottom);
  }

  private multi(): boolean { return this.opponents().length > 1; }

  render(): TemplateResult {
    const bottom = this.bottomSeat();
    const opps = this.opponents();
    const fit = this.fit;
    return html`
      <div style=${fit ? `width: ${fit.w}px; height: ${fit.h}px; transform: scale(${fit.s}); --vw: ${fit.w / 100}px; --vh: ${fit.h / 100}px` : nothing}
          class="table ${opps.length > 1 ? "multi" : ""} ${this.targetCandidates() ? "aiming" : ""} ${this.intents ? "" : "watching"} ${this.inPregame() ? "pregame" : ""} ${fit && fit.h < COMPACT_H ? "compact" : ""}"
          @pointermove=${(e: PointerEvent) => this.onPointerMove(e)} @pointerup=${(e: PointerEvent) => this.onPointerUp(e)} @dragstart=${(e: DragEvent) => e.preventDefault()}>
        ${this.renderBattlefield()}
        <section class="opps">${opps.map((pid) => this.renderOpp(pid))}</section>
        ${this.renderMid()}
        ${this.renderMine(bottom)}
        ${this.renderHand(bottom)}
        ${this.renderMe(bottom)}
        ${this.renderActions()}
        ${this.renderMulligan()}
        ${this.renderPop()}
        ${this.renderLog()}
        ${this.renderMenu()}
        ${this.renderLogMenu()}
        ${this.renderZoneShown()}
        ${this.renderPreview()}
        ${this.renderWhyShown()}
        ${this.renderError()}
        ${this.renderTurnBanner()}
        ${this.renderLookRings()}
        ${arrowLayer(this.arrows)}
        ${this.renderFloaters()}
      </div>
    `;
  }

  private measureHits(before: BoardView): void {
    const layer = this.renderRoot.querySelector(".table");
    if (!layer) return;
    const origin = layer.getBoundingClientRect();
    const k = tableScale(this.renderRoot);
    const dealt = newDamage(before.history, this.view.history);
    this.strikes = dealt.length > 0 ? measureStrikes(this.renderRoot, dealt) : [];
    this.lifeDeltas = before.state === this.view.state ? [] : lifeChanges(before.state, this.view.state);
    const impact = this.strikes.some((s) => s.combat) ? IMPACT_MS : 0;
    const added = [...damageFloaters(this.strikes, origin, k), ...lifeFloaters(this.renderRoot, this.lifeDeltas, origin, k, impact)];
    if (added.length === 0) return;
    this.floaters = [...this.floaters, ...added];
    const ids = new Set(added.map((f) => f.id));
    window.setTimeout(() => { this.floaters = this.floaters.filter((f) => !ids.has(f.id)); }, impact + FLOATER_MS);
  }

  private renderFloaters(): TemplateResult | typeof nothing {
    if (this.floaters.length === 0) return nothing;
    return html`<div class="floater-layer" aria-hidden="true">${repeat(this.floaters, (f) => f.id, (f) => html`<span class="floater ${f.tone}"
        style="left:${f.x}px; top:${f.y}px; animation-delay:${f.delay}ms">${f.text}</span>`)}</div>`;
  }

  private renderLookRings(): TemplateResult | typeof nothing {
    if (this.lookRings.length === 0) return nothing;
    return html`<div class="look-layer" aria-hidden="true">${repeat(this.lookRings, (r) => r.key, (r) => html`<div class="look-ring"
        style="left:${r.x}px; top:${r.y}px; width:${r.w}px; height:${r.h}px">${r.name ? html`<span class="look-name">${r.name}</span>` : nothing}</div>`)}</div>`;
  }

  private renderConcede(): TemplateResult | typeof nothing {
    const concede = this.legal().map(offerAction).find((a) => a.kind === "Concede");
    if (!concede || !this.acting) return nothing;
    return html`<button class="btn danger" @click=${() => { if (confirm("Concede this game?")) this.take(concede); }}>Concede</button>`;
  }

  // Compact opponent portraits keep their clock edge; your corner portrait shows name and life.
  private renderAvatar(pid: string, panel = false): TemplateResult {
    const p = this.st.players[pid]!;
    const deciding = this.decider() === pid;
    const legal = this.isTargetable(playerTarget(pid));
    // While attacking: a player who can be attacked, and the one new attackers go at.
    const d = this.decision;
    const defendable = this.acting && d.kind === "ViewerDeclaresAttackers" && isAttackable(d.values[0], { pid, planeswalker: null });
    const defended = defendable && this.defaultDefender()?.pid === pid && this.defaultDefender()?.planeswalker === null;
    const label = legal ? `Target ${p.name}` : defendable ? `Attack ${p.name}` : nothing;
    const title = [p.name, this.st.active === pid ? "taking this turn" : "", deciding ? "the game waits on them" : "",
      defended ? "new attackers attack them" : ""].filter(Boolean).join(", ");
    // Turn order is shared by every viewer, so a seat keeps the same portrait on either side.
    const portrait = playerPortraitAt(panel ? 0 : Math.max(0, this.st.turn_order.indexOf(pid)));
    const reading = this.view.clocks;
    const clock = playerClock(reading, pid);
    return html`<div class="avatar ${pid === this.view.seat ? "you" : ""} ${deciding ? "deciding" : ""} ${legal ? "legal" : ""} ${defendable ? "defendable" : ""} ${defended ? "defended" : ""}"
        data-anchor="seat:${pid}" role=${legal || defendable ? "button" : nothing} aria-label=${label}
        tabindex=${legal || defendable ? "0" : nothing}
        title=${title} @click=${() => this.clickSeat(pid)}
        @mouseenter=${() => { this.pointedSeat = pid; }} @mouseleave=${() => { if (this.pointedSeat === pid) this.pointedSeat = null; }}
        @keydown=${(e: KeyboardEvent) => { if ((legal || defendable) && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); this.clickSeat(pid); } }}>
      ${panel ? html`<img class="avatar-art" src=${portrait} alt="" draggable="false">`
        : html`<div class="face art" aria-hidden="true" style=${`--face: url("${portrait}")`}></div>`}
      ${panel ? html`<div class="portrait-caption">
        <span class="name ${namePlateClass(this.looksOf(pid).name_plate)}" title=${p.name}>${p.name}</span>
      </div>` : clock ? html`<clock-ring .budget=${reading?.clocks.budget_ms ?? 0} .ms=${clock.left_ms} .running=${clock.running} .since=${reading?.receivedAt ?? 0}></clock-ring>` : nothing}
      <span class="life-plate ${p.life <= this.view.startingLife / 4 ? "low" : ""}" aria-label="${p.life} life"><span class="life" title="Life">${p.life}</span>${panel ? html`<span class="life-label">Life</span>` : nothing}</span>
    </div>`;
  }

  // The player's time left, highlighted while it runs.
  private renderClock(pid: string): TemplateResult | typeof nothing {
    const reading = this.view.clocks;
    const clock = playerClock(reading, pid);
    if (!reading || !clock) return nothing;
    return html`<span class="clock ${clock.running ? "running" : ""}" role="timer"
        title="${playerName(this.st, pid)}'s clock${clock.running ? ", running: the game waits on them" : ""}">
      <clock-time .ms=${clock.left_ms} .running=${clock.running} .since=${reading.receivedAt}></clock-time>
    </span>`;
  }

  // A player's library, graveyard and exile, each a pile with its name and count beneath. Graveyard
  // and exile are public (400.2): the newest card shows on top, the two before it askew underneath, and the
  // pile opens when clicked; one holding a card the viewer can play from there now (715.3d) glows.
  private renderZones(p: PublicPlayer): TemplateResult {
    const pile = (zone: ZoneShown["zone"], name: string, cards: PublicCard[]) => {
      const top = cards.at(-1);
      const usable = cards.some((c) => this.usableFromZone(c.object_id));
      return html`<button class="zone-slot zone-link zone-${zone} ${usable ? "playable" : ""}" data-anchor="zone:${p.id}:${zone}"
          title="${name}: ${cards.length} ${cards.length === 1 ? "card" : "cards"}${usable ? ", one you can play from there now" : ""}"
          ?disabled=${cards.length === 0} @click=${() => { this.zoneShown = { pid: p.id, zone }; }}
          @mouseenter=${(e: MouseEvent) => { this.pointZone(p.id, zone); if (top) this.hover(top.object_id, e); }}
          @mouseleave=${() => { this.pointZone(null, zone); if (top) this.hover(null); }}>
        <span class="pile ${top ? "" : "empty"}" data-anchor=${top ? `obj:${top.object_id}` : nothing}>
          ${cards.slice(-3, -1).map((c) => html`<span class="under">${this.cardArt(c)}</span>`)}
          ${top ? this.cardArt(top) : nothing}
        </span>
        ${zoneCaption(name, cards.length)}
      </button>`;
    };
    // 401.5: a top card the viewer may see is shown face up, and played or cast from there when clicked.
    const top = optionValue(p.library_top);
    const library = top
      ? html`<button class="zone-slot library zone-link ${this.acting && this.actionsOn(top.object_id).length > 0 ? "playable" : ""}" data-anchor="zone:${p.id}:library"
          title="Library: ${p.library_count} ${p.library_count === 1 ? "card" : "cards"}, the top one shown"
          @click=${() => { this.clickObjects([top.object_id]); }}
          @mouseenter=${(e: MouseEvent) => { this.pointZone(p.id, "library"); this.hover(top.object_id, e); }}
          @mouseleave=${() => { this.pointZone(null, "library"); this.hover(null); }}>
          <span class="pile" data-anchor=${`obj:${top.object_id}`}>${this.cardArt(top)}</span>
          ${zoneCaption("Library", p.library_count)}
        </button>`
      : html`<span class="zone-slot library" data-anchor="zone:${p.id}:library" title="Library: ${p.library_count} ${p.library_count === 1 ? "card" : "cards"}"
          @mouseenter=${() => this.pointZone(p.id, "library")} @mouseleave=${() => this.pointZone(null, "library")}>
          <span class="pile ${p.library_count > 0 ? "backs with-back" : "empty"}"
            style="--depth:${Math.min(4, Math.ceil(p.library_count / 15))}px; ${cardBackStyle(this.looksOf(p.id).card_back)}"></span>
          ${zoneCaption("Library", p.library_count)}
        </span>`;
    return html`<div class="zones">
      ${library}
      ${pile("graveyard", "Graveyard", cardsIn(p.graveyard))}
      ${pile("exile", "Exile", cardsIn(p.exile))}
    </div>`;
  }

  private pointZone(pid: string | null, zone: PointedZone["zone"]): void {
    if (pid !== null) this.pointedZone = { pid, zone };
    else if (this.pointedZone?.zone === zone) this.pointedZone = null;
  }

  private renderChips(pid: string): TemplateResult {
    const st = this.st;
    const chips: TemplateResult[] = [];
    if (passedSincePriorityMoved(st).includes(pid)) chips.push(html`<span class="chip passed" title="Passed since the stack or step last changed">Passed</span>`);
    const level = this.view.aiLevels[pid];
    if (level) chips.push(html`<span class="chip ai" title="An AI seat, playing at ${level}">AI</span>`);
    return html`<span class="chips">${chips}${this.renderPregameChips(pid)}${this.renderCommanderChips(pid)}</span>`;
  }

  // 903.8 and 903.10a: each commander the player owns with its tax, and the commander damage they've
  // taken, which 21 from one commander makes lethal.
  private renderCommanderChips(pid: string): TemplateResult[] {
    const st = this.st;
    const p = st.players[pid];
    if (!p) return [];
    const short = (name: string) => name.split(",")[0];
    return [
      ...p.commander_tax.map((t) => html`<span class="chip cmdr" title="${t.commander}: casting it from the command zone costs {${t.tax}} more">♛ ${short(t.commander)}${t.tax > 0 ? ` +${t.tax}` : ""}</span>`),
      ...p.commander_damage.filter((d) => d.damage > 0).map((d) => html`<span class="chip cmdr ${d.damage >= 21 ? "lethal" : ""}"
        title="${d.damage} combat damage from ${d.commander} (${d.owner === this.view.seat ? "yours" : playerName(st, d.owner)}); 21 from one commander loses">⚔ ${d.damage} from ${short(d.commander)}</span>`),
    ];
  }

  private renderOpp(pid: string): TemplateResult {
    const st = this.st;
    const p = st.players[pid]!;
    const g = groupField(st, pid, this.soloOids());
    return html`<div class="opp ${st.active === pid ? "active" : ""} ${p.lost ? "lost" : ""}">
      <div class="pod">
        <div class="ident">
          <div class="who">
            <span class="name ${namePlateClass(this.looksOf(pid).name_plate)}">${p.name}</span>
            ${this.renderClock(pid)}
            ${this.renderChips(pid)}
          </div>
          ${this.renderAvatar(pid)}
        </div>
        ${p.poison_counters > 0 ? html`<span class="poison" title="Poison counters">☠ ${p.poison_counters}</span>` : nothing}
        ${p.speed > 0 ? html`<span class="speed" title=${p.speed >= 4 ? "Max speed" : "Speed"}>Speed ${p.speed}</span>` : nothing}
        ${this.renderHandBacks(p)}
        ${this.renderCommandZone(p)}
        ${this.renderZones(p)}
      </div>
      <div class="field" data-field=${pid}>
        ${this.renderBackRow(g, pid, "opp")}
        <div class="row front" style=${this.rowFit(g.creatures, "opp", "front", this.walkersRoom(g, pid, "opp"))}>${g.creatures.length ? this.renderGroups(g.creatures, pid) : html`<span class="row-empty">no creatures</span>`}</div>
        ${this.renderWalkers(g, pid, "opp")}
      </div>
    </div>`;
  }

  // An opponent's hand: card backs, or its cards where the projection carries them (a spectator's
  // view of an all-AI game, §9.34).
  private renderHandBacks(p: PublicPlayer): TemplateResult {
    const shown = cardsIn(p.hand);
    if (shown.length > 0) {
      return html`<span class="revealed-hand" title="${p.hand_count} cards in hand">${shown.map((c) => html`<span class="revealed" data-anchor="obj:${c.object_id}"
        @mouseenter=${(e: MouseEvent) => this.hover(c.object_id, e)} @mouseleave=${() => this.hover(null)}>${this.cardArt(c)}</span>`)}</span>`;
    }
    const n = Math.min(p.hand_count, 7);
    const mid = (n - 1) / 2;
    const looked = lookedHandBack(this.view.looks, p.id, p.hand_count, n);
    const back = this.looksOf(p.id).card_back;
    return html`<span class="mini-hand" title="${p.hand_count} cards in hand" style=${cardBackStyle(back)}>${Array.from({ length: n }, (_, i) => html`<span class="${i === looked ? "looked" : ""} with-back"
      style="--rot:${(mid - i) * 6}deg; --ty:${-Math.abs(i - mid) * 1.5}px"></span>`)}</span>`;
  }

  // Lands in the row at the battlefield's outer edge, the other non-creatures in a row inside it.
  private renderBackRow(g: GroupedField, pid: string, side: "opp" | "mine"): TemplateResult {
    const row = (groups: PermGroup[]): TemplateResult | typeof nothing => groups.length === 0 ? nothing
      : html`<div class="row back" style=${this.rowFit(groups, side, "back", this.walkersRoom(g, pid, side))}>${this.renderGroups(groups, pid)}</div>`;
    return side === "opp" ? html`${row(g.lands)}${row(g.others)}` : html`${row(g.others)}${row(g.lands)}`;
  }

  // Planeswalkers stand upright in the battlefield's outer corner, as MTGA shows them, filling its
  // height.
  private renderWalkers(g: GroupedField, pid: string, side: "opp" | "mine"): TemplateResult | typeof nothing {
    if (g.planeswalkers.length === 0) return nothing;
    const { cols, w, h } = walkerGrid(g.planeswalkers.length, this.walkerSpace(pid, side));
    return html`<div class="walkers" style="--walker-cols:${cols};--card-w:${w}px;--card-h:${h}px">${this.renderGroups(g.planeswalkers, pid, true)}</div>`;
  }

  // Twice the planeswalkers' width, as a centred row must leave it at both ends to keep clear of them.
  private walkersRoom(g: GroupedField, pid: string, side: "opp" | "mine"): number {
    if (g.planeswalkers.length === 0) return 0;
    const { cols, w } = walkerGrid(g.planeswalkers.length, this.walkerSpace(pid, side));
    return 2 * cols * (w + 10);
  }

  // The room a battlefield gives its planeswalkers: inside its padding as last laid out (else both
  // rows' usual height across the table), and both rows' usual height, the tallest they stand.
  private walkerSpace(pid: string, side: "opp" | "mine"): { height: number; width: number; rows: number } {
    const rows = Math.round(this.rowWidth([], side, "front", 0).width * TILE_RATIO) + 20 + 2 * Math.round(this.rowWidth([], side, "back", 0).width * TILE_RATIO);
    const field = this.renderRoot.querySelector<HTMLElement>(`[data-field="${pid}"]`);
    if (!field) return { height: rows, width: this.renderRoot.querySelector(".table")?.clientWidth || window.innerWidth, rows };
    const pad = getComputedStyle(field);
    return {
      height: Math.max(rows, field.clientHeight - parseFloat(pad.paddingTop) - parseFloat(pad.paddingBottom)),
      width: field.clientWidth - parseFloat(pad.paddingLeft) - parseFloat(pad.paddingRight),
      rows,
    };
  }

  // A row's card size, so it stays on one line however full the battlefield gets: the usual size
  // for the row while there's room, smaller after.
  private rowFit(groups: PermGroup[], side: "opp" | "mine", row: "front" | "back", fixed = 0): string {
    const { width, lines } = this.rowWidth(groups, side, row, fixed);
    return `--card-w:${width}px;--card-h:${Math.round(width * TILE_RATIO)}px${lines > 1 ? ";flex-wrap:wrap" : ""}`;
  }

  private rowWidth(groups: PermGroup[], side: "opp" | "mine", row: "front" | "back", fixed: number): { width: number; lines: number } {
    const multi = this.multi();
    const table = this.renderRoot.querySelector(".table");
    const tableWidth = table?.clientWidth || window.innerWidth;
    const scale = Math.max(0.75, Math.min(1.2, (table?.clientHeight || window.innerHeight) / 960));
    const usual = side === "mine" ? (row === "front" ? (multi ? 108 : 134) : (multi ? 68 : 86))
      : row === "front" ? (multi ? 72 : 114) : (multi ? 50 : 74);
    const widest = Math.round(usual * scale);
    const gap = side === "opp" && multi ? (row === "front" ? 5 : 4) : 10;
    const panels = this.opponents().length;
    const room = side === "mine" ? tableWidth - 64
      : multi ? (tableWidth - 32 - 14 * (panels - 1)) / panels - 20 : tableWidth - 32;
    const attachmentCount = groups.reduce((n, g) => n + attachmentsOf(this.st, g.top.object_id).length, 0);
    return wrappedRowFit(groups, widest, room, gap, fixed, attachmentCount);
  }

  private looksOf(pid: string): SeatLooks {
    return this.view.seatLooks[pid] ?? { card_back: null, card_frame: null, name_plate: null };
  }

  // Whose card it is where it lies: the player whose zone holds it.
  private holderOf(oid: number): string | null {
    const key = String(oid);
    for (const p of Object.values(this.st.players)) {
      if (p.battlefield[key] || p.hand[key] || p.graveyard[key] || p.exile[key] || p.command_zone[key]) return p.id;
    }
    return null;
  }

  // The viewer's battlefield under the whole table, dimmed so the cards stay the brightest thing on
  // it, and its artist credited.
  private renderBattlefield(): TemplateResult | typeof nothing {
    const field = this.view.battlefield;
    if (!field) return nothing;
    return html`<div class="battlefield-art" style=${`--battlefield: url("${battlefieldUrl(field.id)}")`}></div>
      <span class="battlefield-credit">${field.card}, by ${field.artist}</span>`;
  }

  private renderMine(pid: string): TemplateResult {
    const g = groupField(this.st, pid, this.soloOids());
    const dropTarget = this.dragging?.kind === "hand" && this.actionsOn(this.dragging.oid).length > 0;
    return html`<section class="mine field ${dropTarget ? "drop-target" : ""}" data-field=${pid}>
      <div class="row front" style=${this.rowFit(g.creatures, "mine", "front", this.walkersRoom(g, pid, "mine"))}>${g.creatures.length ? this.renderGroups(g.creatures, pid) : html`<span class="row-empty">no creatures</span>`}</div>
      ${this.renderBackRow(g, pid, "mine")}
      ${this.renderWalkers(g, pid, "mine")}
    </section>`;
  }

  // Permanents the board draws on their own: those in combat, so their arrows find them, and a card
  // question's candidates, so each can be told apart and clicked (#191).
  private soloOids(): Set<number> {
    const st = this.st;
    return new Set([
      ...st.attackers.map((a) => a.attacker), ...st.blocks.map((b) => b.blocker), ...(this.cardCandidates() ?? []),
      ...this.attackPlan.map((a) => a.attacker), ...this.blockPlan.map((b) => b.blocker),
      ...(this.pickedBlocker === null ? [] : [this.pickedBlocker]),
    ]);
  }

  // How the board marks a creature in combat: attacking or blocking (declared, or in the plan being
  // built), able to attack or block, the blocker picked, and an attacker it may block.
  private combatClass(oids: number[]): string {
    const st = this.st;
    const d = this.decision;
    const has = (list: number[]) => oids.some((oid) => list.includes(oid));
    const cls: string[] = [];
    const attackers = d.kind === "ViewerDeclaresAttackers" ? this.attackPlan.map((a) => a.attacker) : st.attackers.map((a) => a.attacker);
    const blockers = d.kind === "ViewerDeclaresBlockers" ? this.blockPlan.map((b) => b.blocker) : st.blocks.map((b) => b.blocker);
    if (has(attackers)) cls.push("attacking");
    if (has(blockers)) cls.push("blocking");
    if (!this.acting) return cls.join(" ");
    if (d.kind === "ViewerDeclaresAttackers") {
      if (!has(attackers) && oids.some((oid) => canAttack(d.values[0], oid))) cls.push("can-attack");
      if (oids.some((oid) => isAttackablePlaneswalker(d.values[0], oid))) cls.push("defendable");
    }
    if (d.kind === "ViewerDeclaresBlockers") {
      const picked = this.pickedBlocker;
      if (picked !== null && oids.includes(picked)) cls.push("picked");
      else if (!has(blockers) && oids.some((oid) => isBlocker(d.values[0], oid))) cls.push("can-block");
      const blocker = this.dragging?.kind === "block" ? this.dragging.oid : picked;
      if (blocker !== null && oids.some((oid) => canBlock(d.values[0], blocker, oid))) cls.push("blockable");
    }
    return cls.join(" ");
  }

  // How the board marks a card it's asking about: a target it may pick, a card it may choose, or the
  // card being aimed with.
  private askClass(oids: number[]): string {
    if (this.aim && oids.includes(this.aim.source)) return "aiming";
    if (oids.some((oid) => this.isTargetable(objectTarget(oid)))) return "legal";
    if (oids.some((oid) => this.cardCandidates()?.includes(oid))) return "legal choosable";
    if (this.aim?.picked.some((t) => t.kind === "TargetObject" && oids.includes(t.values[0]))) return "chosen";
    return "";
  }

  private renderGroups(groups: PermGroup[], pid: string, upright = false): TemplateResult[] {
    const taps = pid === this.view.seat ? this.plannedTaps() : new Set<number>();
    return groups.map((g) => this.renderPerm(g, pid, taps, upright));
  }

  private renderPerm(g: PermGroup, pid: string, taps: Set<number>, upright: boolean): TemplateResult {
    const attachments = attachmentsOf(this.st, g.top.object_id);
    if (attachments.length === 0) return this.renderPermCard(g, pid, taps, undefined, upright);
    return html`<div class="perm-group" style="--attachments:${attachments.length}">
      ${attachments.map((a) => {
        const controller = controllerOf(this.st, a.object_id) ?? pid;
        return this.renderPermCard({ key: `solo:${a.object_id}`, cards: [a], top: a }, controller,
          controller === this.view.seat ? this.plannedTaps() : new Set<number>(), cardLabel(g.top));
      })}
      ${this.renderPermCard(g, pid, taps, undefined, upright)}
    </div>`;
  }

  private renderPermCard(g: PermGroup, pid: string, taps: Set<number>, attachedTo?: string, upright = false): TemplateResult {
    const st = this.st;
    const c = g.top;
    const oid = c.object_id;
    const mine = pid === this.view.seat;
    const playable = mine && this.acting && g.cards.some((x) => this.actionsOn(x.object_id).length > 0);
    const willTap = g.cards.filter((x) => taps.has(x.object_id)).length;
    const oids = g.cards.map((x) => x.object_id);
    const ask = this.askClass(oids);
    const combat = this.combatClass(oids);
    const cls = [c.tapped ? "tapped" : "", playable && !ask ? "playable" : "", willTap ? "will-tap" : "", combat, ask, this.paymentClass(oids),
      oids.some((x) => this.arrived.has(x)) ? "arrived" : ""];
    const d = this.decision;
    const pressable = (d.kind === "ViewerDeclaresAttackers" ? oids.find((x) => canAttack(d.values[0], x))
      : d.kind === "ViewerDeclaresBlockers" ? oids.find((x) => isBlocker(d.values[0], x))
      : oids.find((x) => this.actionsOn(x).length > 0)) ?? oid;
    return html`<div class="perm bf-slot ${attachedTo ? "attachment" : ""} ${cls.join(" ")}" data-anchor=${oids.map((x) => `obj:${x}`).join(" ")}
        title=${attachedTo ? `${cardLabel(c)} — attached to ${attachedTo}` : this.permTitle(g)}
        aria-label=${ask.includes("legal") ? `Choose ${cardLabel(c)}` : attachedTo ? `${cardLabel(c)}, attached to ${attachedTo}` : this.permTitle(g)} role="button" tabindex="0"
        @keydown=${(e: KeyboardEvent) => {
          // A mouse click leaves this tile focused. Space must still confirm the current payment
          // or combat declaration through the board shortcut rather than clicking the tile again.
          if (e.key === " " && (this.choiceSpec() || this.declaring())) return;
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); this.clickObjects(oids); }
        }}
        @mouseenter=${(e: MouseEvent) => this.hover(oid, e)} @mouseleave=${() => { if (this.hoverOid === oid) this.hover(null); }}
        @pointerdown=${(e: PointerEvent) => this.pressCard(e, pressable)} @click=${() => this.clickObjects(oids)}>
      ${g.cards.length > 2 ? html`<div class="ghost g2"></div>` : nothing}${g.cards.length > 1 ? html`<div class="ghost g1"></div>` : nothing}
      ${upright ? this.walkerArt(c) : this.tileArt(c)}
      ${attachedTo ? html`<span class="attachment-name" aria-hidden="true">${cardLabel(c)}</span>` : nothing}
      ${g.cards.length > 1 ? html`<span class="count">×${g.cards.length}</span>` : nothing}
      ${this.renderPt(c)}
      ${upright ? nothing : html`<span class="keyword-badges">${renderKeywordBadges(c.characteristics.keywords, this.unmetKeywords(pid))}</span>`}
      ${this.renderBadges(c, pid, g.cards.length, true, !upright)}
    </div>`;
  }

  private unmetKeywords(pid: string): Set<Keyword["kind"]> {
    return this.st.players[pid]?.has_enduring_story === false ? new Set(["Storied"]) : new Set();
  }

  private cardArt(c: PublicCard): TemplateResult {
    const face = knownFace(c);
    const fd = isFaceDown(c);
    const name = fd ? "" : cardLabel(c);
    const oracle = face ? this.oracle.get(face.name) : null;
    const holder = this.holderOf(c.object_id);
    const looks = holder ? this.looksOf(holder) : null;
    return html`<card-art name=${name} ?faceDown=${fd} artKey=${optionValue(c.token_art_key) ?? ""} frame=${frameOf(c)} pt=${face ? ptLabelOf(face, 0) : ""}
      back=${looks?.card_back ?? ""} look=${looks?.card_frame ?? ""}
      .keywords=${fd ? [] : face?.keywords ?? []}
      manaCost=${c.characteristics.cost.length ? costText(c.characteristics.cost) : ""} typeLine=${face ? typeLineOf(face) : ""} oracleText=${oracle?.oracle_text ?? ""}></card-art>`;
  }

  // A permanent as the battlefield shows it: a crop of its art, framed in its colour.
  private tileArt(c: PublicCard): TemplateResult {
    const face = knownFace(c);
    const fd = isFaceDown(c);
    const holder = this.holderOf(c.object_id);
    const looks = holder ? this.looksOf(holder) : null;
    return html`<card-art crop name=${fd ? "" : cardLabel(c)} ?faceDown=${fd} artKey=${optionValue(c.token_art_key) ?? ""}
      back=${looks?.card_back ?? ""} look=${looks?.card_frame ?? ""}
      frame=${frameOf(c)} typeLine=${face ? typeLineOf(face) : ""}></card-art>`;
  }

  // A planeswalker as the battlefield shows it: upright, with its loyalty in a shield.
  private walkerArt(c: PublicCard): TemplateResult {
    const fd = isFaceDown(c);
    const holder = this.holderOf(c.object_id);
    const looks = holder ? this.looksOf(holder) : null;
    return html`<card-art upright name=${fd ? "" : cardLabel(c)} ?faceDown=${fd} artKey=${optionValue(c.token_art_key) ?? ""}
      back=${looks?.card_back ?? ""} frame=${frameOf(c)} loyalty=${String(c.loyalty_counters)}></card-art>`;
  }

  private permTitle(g: PermGroup): string {
    const c = g.top;
    const pt = ptLabelOf(c.characteristics, c.damage);
    return [g.cards.length > 1 ? `${g.cards.length} × ${cardLabel(c)}` : cardLabel(c), pt, c.tapped ? "tapped" : ""].filter(Boolean).join(" · ");
  }

  private renderPt(c: PublicCard): TemplateResult | typeof nothing {
    const p = optionValue(c.characteristics.power);
    const t = optionValue(c.characteristics.toughness);
    if (p === null || t === null) return nothing;
    return html`<span class="pt ${c.damage > 0 ? "hurt" : ""}" title=${c.damage > 0 ? `${p}/${t}, ${c.damage} damage marked` : `${p}/${t}`}>${p}<span class="pt-cut">/</span><span class="pt-t">${t - c.damage}</span></span>`;
  }

  // A permanent's statuses along its top edge, each saying what it is when hovered: commander,
  // counters, summoning sickness (302.6), what was chosen for it, and whether it's a token.
  private renderBadges(c: PublicCard, pid: string, count: number, sickness = true, loyalty = true): TemplateResult {
    const named = Object.entries(c.named_counters).filter(([, n]) => n > 0);
    const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
    const badge = (cls: string, text: string, tip: string) =>
      html`<span class="badge ${cls}" role="img" aria-label=${tip} data-tip=${tip} title="">${text}</span>`;
    return html`<span class="badges">
      ${c.is_commander ? badge("commander", "♛", "Commander") : nothing}
      ${sickness && isSummoningSick(this.st, pid, c) ? badge("sick", "zZ", "Summoning sick: it can't attack or use {T} abilities until its controller's next turn, unless something lets it") : nothing}
      ${c.plus_one_counters ? badge("plus", `+${c.plus_one_counters}`, plural(c.plus_one_counters, "+1/+1 counter", "+1/+1 counters")) : nothing}
      ${c.minus_one_counters ? badge("minus", `−${c.minus_one_counters}`, plural(c.minus_one_counters, "−1/−1 counter", "−1/−1 counters")) : nothing}
      ${loyalty && c.loyalty_counters ? badge("loyalty", `◆${c.loyalty_counters}`, `Loyalty ${c.loyalty_counters}`) : nothing}
      ${c.lore_counters ? badge("lore", "I".repeat(Math.min(c.lore_counters, 4)), `${plural(c.lore_counters, "lore counter", "lore counters")}: chapter ${c.lore_counters}`) : nothing}
      ${named.map(([k, n]) => badge("named", `${n} ${k}`, plural(n, `${k} counter`, `${k} counters`)))}
      ${c.chosen.map((chosen) => { const label = chosenLabel(chosen); return badge("chosen", label.text, label.tip); })}
      ${c.is_token && count === 1 ? badge("token", "Token", "A token: it stops existing when it leaves the battlefield") : nothing}
      ${c.doors.map((d) => badge(d.unlocked ? "door unlocked" : "door locked", d.name.split(" ")[0] ?? d.name,
        d.unlocked ? `${d.name}: unlocked` : `${d.name}: locked; pay ${costText(d.cost)} as a sorcery to unlock it`))}
      ${c.saddled ? badge("saddled", "Saddled", "Saddled until end of turn") : nothing}
      ${c.prepared ? badge("prepared", "Prepared", "Its prepare spell may be cast from exile") : nothing}
      ${c.class_level.kind === "Some" ? badge("class-level", `Level ${c.class_level.values[0]}`, `Class level ${c.class_level.values[0]}`) : nothing}
      ${c.case_solved.kind === "Some" ? badge(c.case_solved.values[0] ? "case solved" : "case unsolved", c.case_solved.values[0] ? "Solved" : "Unsolved", c.case_solved.values[0] ? "This Case is solved" : "This Case isn't solved yet") : nothing}
    </span>`;
  }

  // ------------------------------------------------------------------ the middle band

  private renderMid(): TemplateResult {
    const st = this.st;
    const step = this.shownStep();
    const recent = latestLines(st, this.view.history, 2);
    const askerAtRight = this.view.previewAt !== "left" && !this.askerBesideChoice() && this.askingCard() !== null;
    return html`<section class="mid ${askerAtRight ? "asker-at-right" : ""}">
      <div class="phasebox">
        <div class="phase-heading">
          ${this.inPregame() ? this.renderPregameChip() : html`<span class="turn-chip">Turn ${step.round}, ${step.active === this.view.seat ? "your turn" : `${playerName(st, step.active)}'s turn`}
            <span class="phase-now">${PHASE_LABEL[step.phase.kind]}</span></span>`}
          ${this.renderClock(this.bottomSeat())}
        </div>
        ${this.renderPlayerResources(this.bottomSeat())}
        ${this.renderStops()}
        <div class="ticker">${recent.map((l) => html`<div><b>${this.actorLabel(l)}</b> ${this.logWords(l)}</div>`)}</div>
        ${this.view.catchingUp ? html`<span class="catching-up">Click to skip ahead</span>` : nothing}
        ${this.renderAutoPassCards()}
      </div>
      <div class="center">
        ${!this.aim && (st.stack.length === 0 || this.asking()) ? this.renderQuestion() : nothing}
        ${this.renderOffBoardChoices()}
        ${st.stack.length > 0 ? this.renderStack() : nothing}
      </div>
      <div class="mid-side">
        <div class="table-meta"><slot name="meta"></slot></div>
      </div>
    </section>`;
  }

  // Whether the board is asking the viewer to point at something.
  private asking(): boolean {
    return this.aim !== null || this.targetCandidates() !== null || this.cardCandidates() !== null;
  }

  // Whether a card is drawn where it can be clicked: on a battlefield, in the viewer's hand or
  // command zone, on top of a library it's shown on, or on the stack.
  private onBoard(oid: number): boolean {
    const key = String(oid);
    const me = this.st.players[this.view.seat];
    return Object.values(this.st.players).some((p) => p.battlefield[key] !== undefined || optionValue(p.library_top)?.object_id === oid)
      || me?.hand[key] !== undefined || me?.command_zone[key] !== undefined
      || this.st.stack.some((item) => stackItemObjectId(item) === oid);
  }

  // What the board asks for that isn't drawn on it, a graveyard's card say, as buttons under the
  // question; each says what sets it apart from a card of the same name (#191).
  private renderOffBoardChoices(): TemplateResult | typeof nothing {
    if (!this.acting) return nothing;
    const targets = (this.targetCandidates() ?? []).filter((t) => t.kind === "TargetObject" && !this.onBoard(t.values[0]));
    const cards = this.overlaidCardChoice(this.choiceSpec()) ? [] : (this.cardCandidates() ?? []).filter((oid) => !this.onBoard(oid));
    if (targets.length === 0 && cards.length === 0) return nothing;
    const button = (oid: number, run: () => void) => html`<button class="btn off-board" @click=${run}
        @mouseenter=${(e: MouseEvent) => this.hover(oid, e)} @mouseleave=${() => this.hover(null)}>${this.cardChoiceLabel(oid)}</button>`;
    return html`<div class="choices off-board-choices">
      ${targets.map((t) => button(t.values[0] as number, () => { this.pick(t); }))}
      ${cards.map((oid) => button(oid, () => { this.pickCard(oid); }))}
    </div>`;
  }

  // A card's name and what tells it apart from another of the same name: where it is, tapped,
  // counters, what's attached, and how long it's been on the battlefield.
  private cardChoiceLabel(oid: number): string {
    const st = this.st;
    const c = findCardAnywhere(st, oid);
    if (!c) return lookupCardNameAnywhere(st, oid);
    const key = String(oid);
    const where = Object.values(st.players).find((p) => p.graveyard[key]) ? "graveyard"
      : Object.values(st.players).find((p) => p.exile[key]) ? "exile" : "";
    const attached = attachmentsOf(st, oid).map((a) => cardLabel(a));
    const details = [
      where, c.tapped ? "tapped" : "",
      c.plus_one_counters ? `+1/+1 ×${c.plus_one_counters}` : "", c.minus_one_counters ? `−1/−1 ×${c.minus_one_counters}` : "",
      c.damage ? `${c.damage} damage` : "", attached.length ? `with ${attached.join(", ")}` : "",
      where === "" && controllerOf(st, oid) ? `since turn ${c.entered_battlefield_turn}` : "",
    ].filter(Boolean);
    return details.length ? `${cardLabel(c)} (${details.join(", ")})` : cardLabel(c);
  }

  private targetLabel(t: Target): string {
    return t.kind === "TargetPlayer" && t.values[0] === this.view.seat ? "You" : targetNameLabel(this.st, t);
  }

  private actorLabel(l: LogLine): string {
    return l.seat === this.view.seat ? "You" : l.actor;
  }

  // A turn the board hasn't shown before is announced while it starts, which the paced updates hold
  // for as long (./paced-updates).
  private announceTurn(): void {
    if (this.inPregame()) return;
    const step = this.shownStep();
    const later = this.shownTurn !== null && step.turn > this.shownTurn;
    this.shownTurn = step.turn;
    if (!later || this.view.turnBannerMs <= 0) return;
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    this.turnBanner = step;
    this.bannerTimer = window.setTimeout(() => { this.bannerTimer = null; this.turnBanner = null; }, this.view.turnBannerMs);
  }

  // The turn and step the board shows: the state's, or one the Room passed through (./paced-updates).
  private shownStep(): TurnStep {
    return this.view.walking ?? stepOf(this.st);
  }

  // The step the board shows on its way to a later update: one the Room passed through with nothing
  // changing, or an update with a newer one waiting. Null once the board is at the latest.
  private passingStep(): TurnStep | null {
    return this.view.walking ?? (this.view.catchingUp ? stepOf(this.st) : null);
  }

  // Why the Room passed for the viewer in the step the board is passing through, or null if it
  // didn't: a decision of theirs on a board a newer update follows was the Room's to answer.
  private passedFor(step: TurnStep): string | null {
    const d = this.decision.kind;
    const passed = this.view.walking
      ? this.view.history.some((e) => e.seat === this.view.seat && isPass(e.answer) && sameStep(e.step, step))
      : d === "ViewerActs" || d === "ViewerDeclaresAttackers" || d === "ViewerDeclaresBlockers";
    if (!passed) return null;
    const row = step.active === this.view.seat ? this.view.pacing?.stops.mine : this.view.pacing?.stops.others;
    return row?.steps.some((s) => s.kind === step.phase.kind) ? "nothing to play" : "not one of your stops";
  }

  // Two rows, as on MTGO: the steps you stop in on your own turn and on everyone else's, when you
  // have something to do there. The Room keeps them for your later games.
  private renderStops(): TemplateResult | typeof nothing {
    const pacing = this.view.pacing;
    if (!pacing || !this.seated()) return nothing;
    const stops = pacing.stops;
    const step = this.shownStep();
    const yourTurn = step.active === this.view.seat;
    const editable = this.intents !== null;
    const row = (title: string, key: "mine" | "others", r: StopRow) => html`
      <div class="stops-row">
        <span class="lbl">${title}</span>
        ${STOP_STEPS.map((stop) => {
          const on = r.steps.some((s) => s.kind === stop.kind);
          const now = stop.kind === step.phase.kind && (key === "mine") === yourTurn;
          return html`<button class="stop ${on ? "on" : ""} ${now ? "now" : ""}" aria-label=${stop.label} aria-pressed=${on ? "true" : "false"}
            title="${title}: ${stop.label}${on ? " (stop)" : ""}${now ? " · now" : ""}" ?disabled=${!editable}
            @click=${() => this.setStops({ ...stops, [key]: { ...r, steps: toggledStep(r.steps, stop.kind) } })}></button>`;
        })}
        <span></span>
      </div>`;
    return html`
      <div class="stops" role="group" aria-label="Stops">
        <span></span>
        <div class="stopkeys">${STOP_STEPS.map((s) => html`<span class=${s.kind === step.phase.kind ? "on" : ""}>${s.short}</span>`)}</div>
        <button class="stops-gear" title="Stop options" aria-label="Stop options" aria-expanded=${this.stopsOpen ? "true" : "false"}
          @click=${() => { this.stopsOpen = !this.stopsOpen; }}>⚙</button>
        ${row("Your turn", "mine", stops.mine)}
        ${row("Theirs", "others", stops.others)}
      </div>
      ${this.stopsOpen ? html`<div class="stops-options">
        <label><input type="checkbox" .checked=${stops.mine.responses} ?disabled=${!editable}
          @change=${(e: Event) => this.setStops({ ...stops, mine: { ...stops.mine, responses: (e.target as HTMLInputElement).checked } })} />
          On your turn, stop when someone acts and you can respond</label>
        <label><input type="checkbox" .checked=${stops.others.responses} ?disabled=${!editable}
          @change=${(e: Event) => this.setStops({ ...stops, others: { ...stops.others, responses: (e.target as HTMLInputElement).checked } })} />
          On theirs, stop when someone acts and you can respond</label>
        <label><input type="checkbox" .checked=${stops.skip_turn_shown} ?disabled=${!editable}
          @change=${(e: Event) => this.setStops({ ...stops, skip_turn_shown: (e.target as HTMLInputElement).checked })} />
          Show "Skip turn", which passes even when an opponent acts</label>
      </div>` : nothing}
    `;
  }

  private renderAutoPassCards(): TemplateResult {
    const cards = this.autoPassCards();
    const answers = this.autoAnswers();
    return html`${cards.length === 0 ? nothing : html`<div class="auto-passes" aria-label="Auto-pass cards">
      ${cards.map((name) => html`<button class="auto-pass" title="Passing to ${name} on the stack; click to stop" ?disabled=${!this.intents}
        @click=${() => this.toggleAutoPass(name)}>pass to ${name} ✕</button>`)}
    </div>`}${answers.length === 0 ? nothing : html`<div class="auto-passes" aria-label="Auto-answer cards">
      ${answers.map((a) => {
        const word = a.answer.kind === "AlwaysYes" ? "yes" : "no";
        return html`<button class="auto-pass" title="Answering ${word} whenever ${a.card} asks; click to be asked again" ?disabled=${!this.intents}
          @click=${() => this.setAutoAnswer(a.card, null)}>always ${word} to ${a.card} ✕</button>`;
      })}
    </div>`}`;
  }

  // Top first: the stack resolves from the left. A spell shows its card, an ability its ability
  // card, each with its kind and what it targets.
  private renderStack(): TemplateResult {
    const st = this.st;
    const items = st.stack.slice().reverse();
    return html`<span class="stack-head">The stack, resolving from the left</span>
      <div class="stack ${items.length > 5 ? "crowded" : ""}">
        ${repeat(items, (item) => stackItemObjectId(item), (item, i) => {
          const id = stackItemObjectId(item);
          const kind = stackItemKind(item);
          const controller = stackItemController(item);
          const card = stackItemCard(item);
          const name = stackItemName(item);
          const short = name.split(",")[0];
          const faded = this.hoverStack !== null && this.hoverStack !== id;
          const passingTo = this.autoPassCards().includes(name);
          return html`<div class="sitem stack-item ${kind !== "Spell" ? "ability" : ""} ${i === 0 ? "top" : ""} ${faded ? "faded" : ""} ${this.arrived.has(id) ? "arrived" : ""} ${this.askClass([id])}"
              data-anchor="stack:${id} obj:${id}" aria-label="${kind}: ${cardLabel(card)}"
              @mouseenter=${(e: MouseEvent) => { this.hoverStack = id; this.hover(id, e); }}
              @mouseleave=${() => { this.hoverStack = null; if (this.hoverOid === id) this.hover(null); }}
              @click=${() => this.clickObjects([id])}>
            <span class="kind ${kind}">${kind}</span>
            <div class="art">${item.kind === "AbilityItem" ? this.abilityCard(item.values[0]) : this.cardArt(card)}</div>
            ${controller !== this.view.seat && this.intents ? html`<button class="more" title="Auto-answers for ${short}" aria-label="Auto-answers for ${short}"
              @click=${() => { this.stackMenu = this.stackMenu === id ? null : id; }}>⋯</button>` : nothing}
            ${this.stackMenu === id ? html`<div class="menu" role="menu">
              <button role="menuitem" @click=${() => this.toggleAutoPass(name)}>${passingTo ? `Stop passing to ${short}` : `Always pass to ${short}`}</button>
            </div>` : nothing}
            <div class="cap"><b>${kind === "Spell" ? cardLabel(card) : `${short} ${kind === "Triggered" ? "trigger" : "ability"}`}</b>
              <span class="by">${controller === this.view.seat ? "Yours" : playerName(st, controller)}</span>
              ${stackItemTargets(item).map((t) => html`<span class="tg">→ ${this.targetLabel(t)}</span>`)}</div>
          </div>`;
        })}
      </div>
      ${this.renderPriorityOrder()}`;
  }

  // Who gets priority next round the table, and who has passed since the stack last changed.
  private renderPriorityOrder(): TemplateResult {
    const st = this.st;
    const passed = passedSincePriorityMoved(st);
    return html`<div class="order" aria-label="Priority order">Priority
      ${priorityOrder(st).map((pid) => html`<span class="seat ${passed.includes(pid) ? "done" : ""} ${st.priority === pid ? "now" : ""}">${pid === this.view.seat ? "You" : playerName(st, pid)}</span>`)}
    </div>`;
  }

  private renderQuestion(): TemplateResult {
    const q = this.question();
    return html`<div class="question ${q.waiting ? "waiting" : ""}">${q.title}${q.detail ? html`<small>${q.detail}</small>` : nothing}</div>`;
  }

  private question(): { title: string; detail: string; waiting?: boolean } {
    const st = this.st;
    const d = this.decision;
    const walking = this.passingStep();
    if (walking) {
      const why = this.passedFor(walking);
      const whose = walking.active === this.view.seat ? "Your" : `${playerName(st, walking.active)}'s`;
      return { title: `${whose} ${PHASE_LABEL[walking.phase.kind].toLowerCase()}`, detail: why ? `Passed for you: ${why}.` : "", waiting: true };
    }
    const spec = this.choiceSpec();
    if (spec?.kind === "ChooseTarget") {
      return { title: spec.values[0].kind === "UpTo" ? "Choose a target, or none" : "Choose a target", detail: "Click a glowing target." };
    }
    if (spec?.kind === "ChooseCard") {
      // A graveyard's cards aren't drawn on the board, so they're buttons under the question.
      const drawn = spec.values[1].filter((oid) => this.onBoard(oid)).length;
      const detail = this.overlaidCardChoice(spec) ? "Click one of the cards."
        : drawn === spec.values[1].length ? "Click one of the glowing cards."
        : drawn === 0 ? "Click one of the cards below." : "Click one of the glowing cards, or one below.";
      return { title: spec.values[0] || "Choose a card", detail };
    }
    if (spec?.kind === "ChooseDamageAssignment") return { title: "", detail: "" };
    switch (d.kind) {
      case "AnotherPlayerDecides":
        return { title: `Waiting for ${playerName(st, d.values[0])} to ${waitingLabel(d.values[1].kind)}…`, detail: "", waiting: true };
      case "ViewerDeclaresAttackers": {
        const to = this.defaultDefender();
        const at = to === null ? "" : to.planeswalker !== null ? combatLabel(st, to.planeswalker) : playerName(st, to.pid);
        const must = d.values[0].filter((g) => g.must_attack).flatMap((g) => g.attackers);
        return {
          title: d.values[0].length === 0 ? "Nothing can attack" : "Declare attackers",
          detail: d.values[0].length === 0 ? "" : `Click a creature to attack ${at}, or drag it onto the player or planeswalker it attacks.`
            + (must.length === 0 ? "" : ` Must attack if able: ${listed(st, must)}.`),
        };
      }
      case "ViewerDeclaresBlockers":
        return {
          title: d.values[0].groups.length === 0 ? "Nothing can block" : "Declare blockers",
          detail: this.pickedBlocker !== null ? `Now click the attacker ${combatLabel(st, this.pickedBlocker)} blocks.`
            : ["Click one of your creatures, then the attacker it blocks; or drag it onto the attacker. Any number can block one attacker.",
              ...this.blockAskText(d.values[0])].join(" "),
        };
      case "ViewerChooses":
        return { title: "", detail: "" };
      case "ViewerActs": {
        const mine = st.active === this.view.seat;
        const main = st.phase.kind === "Main" || st.phase.kind === "Main2";
        if (mine && main) return { title: `Your ${PHASE_LABEL[st.phase.kind].toLowerCase()}`, detail: `Play a card, or pass to ${nextStepLabel(st.phase.kind).toLowerCase()}.` };
        return { title: `${PHASE_LABEL[st.phase.kind]}: you have priority`, detail: "Play something, or pass." };
      }
      default:
        return { title: "", detail: "" };
    }
  }

  // ------------------------------------------------------------------ your side

  private renderHand(pid: string): TemplateResult {
    const p = this.st.players[pid];
    if (this.pregameLayout()) return html`<div class="handzone"></div>`;
    const cards = p ? cardsIn(p.hand) : [];
    if (pid === this.view.seat) {
      const order = new Map(this.handOrder.map((oid, i) => [oid, i]));
      cards.sort((a, b) => (order.get(a.object_id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.object_id) ?? Number.MAX_SAFE_INTEGER));
    }
    const n = cards.length;
    if (n === 0) return html`<div class="handzone">${p && pid === this.view.seat ? html`<span class="hand-empty">Empty hand</span>` : nothing}</div>`;
    const mid = (n - 1) / 2;
    const tilt = n > 1 ? Math.min(3, 8 / Math.max(1, mid)) : 0;
    // Leave room for the actual controls, including longer action labels.
    const least = this.multi() ? HAND_CARD_LEAST_W - 8 : HAND_CARD_LEAST_W;
    const table = this.renderRoot.querySelector<HTMLElement>(".table");
    const width = table?.clientWidth || window.innerWidth;
    const panel = this.renderRoot.querySelector<HTMLElement>(".me");
    const actions = this.renderRoot.querySelector<HTMLElement>(".actions");
    const left = (panel?.offsetLeft ?? 12) + (panel?.offsetWidth ?? 292) + 20;
    const right = actions ? width - actions.offsetLeft + 20 : 340;
    const room = Math.max(least, width - left - right - 24);
    const wanted = Math.min(HAND_CARD_MOST_W, width * HAND_CARD_SCREEN_SHARE);
    const cardW = Math.round(Math.max(least, Math.min(wanted, room / (1 + HAND_CARD_UNCOVERED * (n - 1)))));
    const spread = n > 1 ? Math.min(cardW * HAND_CARD_UNCOVERED, (room - cardW) / (n - 1)) : 0;
    return html`<div class="handzone" style="margin-left:${left}px; margin-right:${right}px"><div class="hand" style="--card-w:${cardW}px; --card-h:${Math.round(cardW * CARD_RATIO)}px; --hand-width:${cardW + spread * (n - 1)}px; --hand-step:${spread}px; --inspect-scale:${Math.max(1.2, 320 / cardW).toFixed(3)}">
      ${repeat(cards, (c) => c.object_id, (c, i) => {
        const oid = c.object_id;
        const playable = this.acting && this.actionsOn(oid).length > 0;
        const ask = this.askClass([oid]);
        const handDrag = this.dragging?.kind === "hand" && this.dragging.oid === oid;
        const dragOrigin = handDrag ? this.handDragOrigin : null;
        const rotation = dragOrigin?.rotation ?? (i - mid) * tilt;
        const offsetY = dragOrigin?.offsetY ?? Math.abs(i - mid) * 4;
        // The card's hand slot moves during a reorder, so subtract that shift to keep it under the pointer.
        const k = handDrag ? tableScale(this.renderRoot) : 1;
        const dx = handDrag && dragOrigin && this.pressed && this.cursor
          ? (this.cursor.x - this.pressed.x) / k - (i - dragOrigin.index) * spread : 0;
        const dy = handDrag && this.pressed && this.cursor ? (this.cursor.y - this.pressed.y) / k : 0;
        return html`<div class="hand-card ${playable && !ask ? "playable" : ""} ${ask} ${handDrag ? "dragging" : ""}" data-anchor="obj:${oid}" data-oid=${oid}
            style="--order:${i}; --rot:${rotation}deg; --ty:${offsetY}px; --drag-x:${dx}px; --drag-y:${dy}px"
            title=${playable ? `${c.characteristics.name}: click or drag to your battlefield to play` : c.characteristics.name}
            @mouseenter=${(e: MouseEvent) => this.hover(oid, e)} @mouseleave=${() => { if (this.hoverOid === oid) this.hover(null); }}
            @pointerdown=${(e: PointerEvent) => this.pressCard(e, oid, true)} @click=${() => this.clickObjects([oid])}>
          <div class="hand-face">${this.cardArt(c)}${c.characteristics.types.some((t) => t.kind === "Land") ? nothing : renderManaPips(c.characteristics.cost)}</div>
        </div>`;
      })}
    </div></div>`;
  }

  private renderMe(pid: string): TemplateResult | typeof nothing {
    const st = this.st;
    const p = st.players[pid];
    if (!p) return nothing;
    return html`<div class="me ${st.active === pid ? "active" : ""} ${this.decider() === pid ? "deciding" : ""}">
      ${this.renderAvatar(pid, true)}
      ${this.renderZones(p)}
      ${this.renderCommandZone(p)}
    </div>`;
  }

  private renderPlayerResources(pid: string): TemplateResult | typeof nothing {
    const p = this.st.players[pid];
    if (!p) return nothing;
    const floating = MANA_ORDER.filter(([kind]) => p.mana_pool[kind] > 0);
    return html`<div class="player-resources">
      ${this.renderChips(pid)}
      ${p.poison_counters > 0 ? html`<span class="poison" title="Poison counters">☠ ${p.poison_counters}</span>` : nothing}
      ${p.speed > 0 ? html`<span class="speed" title=${p.speed >= 4 ? "Max speed" : "Speed"}>Speed ${p.speed}</span>` : nothing}
      ${floating.map(([kind, symbol]) => html`<span class="pip ${symbol}" title=${`${p.mana_pool[kind]} ${kind} floating`}>${symbol}${p.mana_pool[kind] > 1 ? p.mana_pool[kind] : ""}</span>`)}
    </div>`;
  }

  // 903.6: the command zone, where a commander waits to be cast (and the viewer may cast theirs).
  private renderCommandZone(p: PublicPlayer): TemplateResult | typeof nothing {
    const command = cardsIn(p.command_zone);
    if (command.length === 0) return nothing;
    return html`<div class="cmd-minis" aria-label="${p.name}'s command zone">${command.map((c) => html`<div class="cmd-mini perm ${this.actionsOn(c.object_id).length && this.acting ? "playable" : ""} ${this.askClass([c.object_id])}"
        data-anchor="obj:${c.object_id}" title="${c.is_emblem ? "Emblem" : "In the command zone"}: ${c.characteristics.name}"
        @mouseenter=${(e: MouseEvent) => this.hover(c.object_id, e)} @mouseleave=${() => this.hover(null)}
        @pointerdown=${(e: PointerEvent) => this.pressCard(e, c.object_id)} @click=${() => this.clickObjects([c.object_id])}>${this.cardArt(c)}</div>`)}</div>`;
  }

  // As on Arena, two buttons: the big one for the call the game is waiting on, and the likeliest other
  // call above it, with the rest in its menu. Paying, holding priority and undo sit quietly over them.
  private renderActions(): TemplateResult | typeof nothing {
    if (!this.acting || this.inPregame()) return nothing;
    const { primary, side } = this.paceButtons();
    const [second, ...more] = [...side, ...this.undoAllButton()];
    const passing = this.view.pacing ? passingNow(this.view.pacing, this.st) : null;
    const kbd = (b: SideButton) => b.kbd ? html`<kbd>${b.kbd}</kbd>` : nothing;
    return html`<div class="actions">
      <div class="rail">
        ${passing && passing.kind !== "AtStops" ? html`<span class="pace-chip pulse">${passingLabel(passing)}</span>` : nothing}
        <label class="switch" title="Pay for spells and abilities with the sources the game picks; off, you pick them">
          <input type="checkbox" .checked=${this.view.autoPay} @change=${(e: Event) => this.intents?.setAutoPay((e.target as HTMLInputElement).checked)} />Auto-pay
        </label>
        <button class="switch ${this.holdNext ? "on" : ""}" title="Keep priority after your next spell or ability, as holding ${HOLD_KEY} while you play it does"
          aria-label="Hold priority" aria-pressed=${this.holdNext ? "true" : "false"} @click=${() => { this.holdNext = !this.holdNext; }}>Hold</button>
        ${this.renderUndo()}
      </div>
      <div class="duo">
      ${second ? html`<div class="second">
        <button class="btn" title=${second.title} aria-label=${second.name ?? second.label} ?disabled=${second.disabled ?? false}
          @click=${() => { this.moreOpen = false; second.run(); }}>${second.label}${kbd(second)}</button>
        ${more.length > 0 ? html`<button class="btn more-calls" title="More calls" aria-label="More calls" aria-expanded=${this.moreOpen ? "true" : "false"}
          @click=${() => { this.moreOpen = !this.moreOpen; }}>▾</button>` : nothing}
        ${this.moreOpen && more.length > 0 ? html`<div class="menu calls" aria-label="More calls">
          ${more.map((b) => html`<button title=${b.title} aria-label=${b.name ?? b.label} ?disabled=${b.disabled ?? false}
            @click=${() => { this.moreOpen = false; b.run(); }}>${b.label}${kbd(b)}</button>`)}
        </div>` : nothing}
      </div>` : nothing}
      ${primary ? html`<button class="primary ${primary.waiting ? "waiting" : ""}" title=${primary.hint} aria-label=${primary.label} ?disabled=${primary.run === null}
          @click=${() => primary.run?.()}>${primary.label}<span class="hint">${primary.hint}</span></button>` : nothing}
      </div>
    </div>`;
  }

  // Undo (#200): the viewer's last action since they last passed priority, or every one of them;
  // disabled with the Room's reason when it won't.
  private renderUndo(): TemplateResult {
    const actions = this.undoableActions();
    const refusal = this.view.undoable ? optionValue(this.view.undoable.refusal) : null;
    const title = actions > 0 ? `Take back your last action since you passed priority (${UNDO_KEY})` : sentence(refusal ?? "nothing to undo yet");
    return html`<button class="switch" title=${title} aria-label="Undo" ?disabled=${actions === 0} @click=${() => this.undoLastAction()}>Undo<kbd>${UNDO_KEY}</kbd></button>`;
  }

  private undoAllButton(): SideButton[] {
    const actions = this.undoableActions();
    return actions > 1 ? [{
      label: `Undo all ${actions}`, name: "Undo all", title: `Take back all ${actions} of your actions since you passed priority`,
      run: () => this.intents?.undoToLastPass(),
    }] : [];
  }

  private turnOfLabel(pid: string): string {
    if (pid === this.view.seat) return "Your turn";
    return this.multi() ? `${playerName(this.st, pid)}'s turn` : "Opponent's turn";
  }

  private paceButtons(): { primary: Pace | null; side: SideButton[] } {
    const st = this.st;
    const d = this.decision;
    const me = st.players[this.view.seat];
    const walking = this.passingStep();
    if (walking) {
      const why = this.passedFor(walking);
      const whose = walking.active === this.view.seat ? "your turn" : `${playerName(st, walking.active)}'s turn`;
      return { primary: why ? { label: "Passed for you", hint: why, run: null, waiting: true }
        : walking.active === this.view.seat ? { label: PHASE_LABEL[walking.phase.kind], hint: whose, run: null, waiting: true }
        : { label: this.turnOfLabel(walking.active), hint: PHASE_LABEL[walking.phase.kind], run: null, waiting: true }, side: [] };
    }
    const endTurn: SideButton = {
      label: "End turn", kbd: "Enter", title: "Pass to the end of the turn, stopping if an opponent acts and you can respond",
      run: () => this.intents?.endTurn(st.turn), disabled: this.view.pacing?.passing.kind === "EndingTurn" && passingNow(this.view.pacing, st).kind === "EndingTurn",
    };
    const skipTurn: SideButton = {
      label: "Skip turn", kbd: "Shift+Enter", title: "Pass to the end of the turn even if an opponent acts",
      run: () => this.intents?.skipTurn(st.turn),
    };
    const resolving = this.view.pacing !== null && passingNow(this.view.pacing, st).kind === "ResolvingStack";
    const resolveAll: SideButton = {
      label: "Resolve all", kbd: "Shift+Space", title: "Pass until what's on the stack now has resolved, stopping if something new goes on it you can respond to",
      run: () => this.resolveAll(), disabled: resolving,
    };
    if (this.aim) {
      const done = aimComplete(this.aim);
      const none = this.aim.picked.length === 0;
      return {
        primary: done ? { label: none ? "No target" : "Done", hint: none ? "Use it without a target" : "Use it with the targets chosen", run: () => this.take(done) }
          : { label: "Choose targets", hint: "on the board", run: null, waiting: true },
        side: [{ label: "Cancel", kbd: "Esc", title: "Put it back", run: () => this.cancelAim() }],
      };
    }
    const pacingSide = (): SideButton[] => me && !me.lost
      ? [endTurn, ...(st.stack.length > 0 ? [resolveAll] : []), ...(this.view.pacing?.stops.skip_turn_shown ? [skipTurn] : [])] : [];
    const spec = this.choiceSpec();
    if (spec?.kind === "ChooseTarget") {
      const none: SideButton[] = spec.values[0].kind === "UpTo" ? [{ label: "No target", title: "Choose no target", run: () => this.answer({ kind: "ChoseNoTarget", values: [] }) }] : [];
      return { primary: { label: "Choose a target", hint: "on the board", run: null, waiting: true }, side: none };
    }
    if (spec?.kind === "ChooseCard") return { primary: { label: "Choose a card", hint: "on the board", run: null, waiting: true }, side: [] };
    if (spec?.kind === "ChooseDamageAssignment") {
      const amounts = damageInOrder(spec.values[0], this.damageOrder);
      return { primary: { label: "Assign damage", hint: "Deal it (Space)", run: () => this.answer({ kind: "ChoseDamageAssignment", values: [amounts] }) }, side: [] };
    }
    switch (d.kind) {
      case "ViewerActs": {
        const run = d.values[0].some((o) => offerAction(o).kind === "PassPriority") ? () => this.passPriority() : null;
        const next = nextStepLabel(st.phase.kind);
        const ownStep = st.active === this.view.seat && st.phase.kind !== "End" && st.phase.kind !== "Cleanup";
        const primary: Pace = st.stack.length > 0 ? { label: "Resolve", hint: "Pass priority (Space)", run }
          : ownStep ? { label: `To ${next}`, hint: "Pass priority (Space)", run }
          : { label: "Pass", hint: `On to ${next.toLowerCase()} (Space)`, run };
        const loop: SideButton[] = optionValue(st.repeatable_loop) ? [{
          label: "Repeat loop…", title: "You're back where a loop of yours began: take it round again, as many times as you name",
          run: () => { this.loopPromptOpen = true; },
        }] : [];
        return { primary, side: [...pacingSide(), ...loop] };
      }
      case "ViewerDeclaresAttackers": {
        const n = this.attackPlan.length;
        const plan = this.attackPlan;
        const all = d.values[0].reduce((sum, g) => sum + g.attackers.length, 0);
        return {
          primary: n > 0
            ? { label: `Attack (${n})`, hint: "Declare these attackers (Space)", run: () => this.declareAttacks(plan) }
            : all > 0
              ? { label: "Attack all", hint: "Pick every attacker (Space)", run: () => this.attackAll() }
              : { label: "No attacks", hint: "Declare no attackers (Space)", run: () => this.declareAttacks([]) },
          side: [
            ...(n > 0 || all > 0 ? [{ label: "No attacks", title: "Declare no attackers", run: () => this.declareAttacks([]) }] : []),
            ...(n > 0 && n < all ? [{ label: "Attack all", title: "Every creature that can attack does", run: () => this.attackAll() }] : []),
          ],
        };
      }
      case "ViewerDeclaresBlockers": {
        const n = this.blockPlan.length;
        const plan = this.blockPlan;
        return {
          primary: n > 0
            ? { label: `Block (${n})`, hint: "Declare these blocks (Space)", run: () => this.declareBlocks(plan) }
            : { label: "No blocks", hint: "Declare no blockers (Space)", run: () => this.declareBlocks([]) },
          side: n > 0 ? [{ label: "No blocks", title: "Declare no blockers", run: () => this.declareBlocks([]) }] : [],
        };
      }
      case "ViewerChooses":
        return this.questionButtons(d.values[0]);
      case "AnotherPlayerDecides": {
        const hint = `Waiting for ${playerName(st, d.values[0])} to ${waitingLabel(d.values[1].kind)}`;
        return { primary: { label: st.active === this.view.seat ? "Waiting" : this.turnOfLabel(st.active), hint, run: null, waiting: true }, side: pacingSide() };
      }
      default:
        return { primary: null, side: [] };
    }
  }

  // A question's confirm is the big button too: the pop above it asks, the button answers.
  private questionButtons(spec: PublicChoiceSpec): { primary: Pace | null; side: SideButton[] } {
    if (this.choiceHidden()) return { primary: { label: "Back to choice", hint: "Show the choice again", run: () => { this.hiddenChoice = null; } }, side: [] };
    const choose = (label: string): Pace => ({ label, hint: "above", run: null, waiting: true });
    switch (spec.kind) {
      case "ChooseMode":
      case "MustChooseFromRevealed":
        return { primary: choose("Choose one"), side: [] };
      case "ChooseTriggerOrder":
        return { primary: choose("Order triggers"), side: [] };
      case "ChooseOptional":
        return { primary: choose("Yes or no"), side: [] };
      case "ChooseFromRevealed":
        return { primary: choose("Choose one"), side: [{ label: "Decline", title: "Choose none of them", run: () => this.answer({ kind: "ChoseFromRevealed", values: [{ kind: "None", values: [] }] }) }] };
      case "ChooseInt":
        return { primary: { label: "Confirm", hint: "Choose that number (Enter)", run: () => this.submitNumber(spec.values[0], spec.values[1]) }, side: [] };
      case "ChooseName":
      case "ChooseNonlandCardName":
      case "ChooseCreatureType": {
        const name = this.namePick;
        return { primary: { label: "Confirm", hint: `Choose that ${nameQuestionNoun(spec)} (Enter)`, run: name ? () => this.answerName(name) : null }, side: [] };
      }
      case "ChoosePayment": {
        const payment = spec.values[0];
        if (this.view.autoPaying) return { primary: { label: "Paying…", hint: "auto-pay", run: null, waiting: true }, side: [] };
        const taps = this.payPicks;
        return {
          primary: { label: "Pay", hint: `${costText(payment.need.cost)} with the sources picked`, run: () => this.answer({ kind: "ChosePayment", values: [taps] }) },
          side: [{ label: "Suggested", title: "The sources auto-pay picks", run: () => { this.payPicks = [...payment.suggested]; } }],
        };
      }
      case "ChooseToTap": {
        if (this.view.autoPaying) return { primary: { label: "Paying…", hint: "auto-pay", run: null, waiting: true }, side: [] };
        const payment = spec.values[0];
        const picks = this.tapPicks;
        const part = optionValue(payment.part_of);
        if (part) return {
          primary: { label: "Tap", hint: `${payment.cost}: ${picks.length} of ${costText(part.cost)} by tapping, mana for the rest`, run: () => this.answer({ kind: "ChoseToTap", values: [picks] }) },
          side: [{ label: "Suggested", title: "What the game suggests tapping", run: () => { this.tapPicks = [...payment.suggested]; } }],
        };
        const total = tappedTotal(payment, picks);
        return {
          primary: { label: "Tap", hint: payment.at_least > 0 ? `${payment.cost}: power ${total} of ${payment.at_least}` : `${payment.cost}: ${picks.length} picked`, run: total >= payment.at_least ? () => this.answer({ kind: "ChoseToTap", values: [picks] }) : null },
          side: [{ label: "Suggested", title: "The creatures the game suggests", run: () => { this.tapPicks = [...payment.suggested]; } }],
        };
      }
      case "ChooseArrangement": {
        const a = spec.values[0];
        const piles = this.arrangedPiles(a);
        return {
          primary: { label: "Done", hint: "Put the cards there", run: arrangementFits(a, piles) ? () => this.answer({ kind: "ChoseArrangement", values: [piles] }) : null },
          side: [{ label: "Suggested", title: "Where the game suggests they go", run: () => { this.arranged = null; } }],
        };
      }
      // Asked elsewhere: on the board itself, or by the damage assignment above.
      case "ChooseTarget":
      case "ChooseCard":
      case "ChooseDamageAssignment":
        return { primary: choose("Answer the question"), side: [] };
    }
  }

  private arrangedPiles(a: PublicArrangement): number[][] {
    return this.arranged?.question === a ? this.arranged.piles : suggestedPiles(a);
  }

  // Max by default: the usual X intent is "as much as I can".
  private submitNumber(min: number, max: number): void {
    const el = this.renderRoot.querySelector<HTMLInputElement>("#x-input");
    const n = el ? parseInt(el.value, 10) : max;
    this.answer({ kind: "ChoseInt", values: [Math.max(min, Math.min(max, Number.isFinite(n) ? n : min))] });
  }

  private answerName(name: string): void {
    this.answer({ kind: "ChoseName", values: [name] });
  }

  // 601.2g-h: while paying by hand, the sources it could tap glow on the battlefield and those picked
  // say TAP; a click on one picks or drops it.
  private paymentClass(oids: number[]): string {
    const spec = this.choiceSpec();
    if (spec?.kind === "ChooseToTap" && this.acting && !this.view.autoPaying) {
      if (this.tapPicks.some((oid) => oids.includes(oid))) return "will-tap";
      return spec.values[0].candidates.some((c) => oids.includes(c.object_id)) ? "payable" : "";
    }
    if (spec?.kind !== "ChoosePayment" || this.view.autoPaying || !this.acting) return "";
    if (this.payPicks.some((t) => oids.includes(t.object_id))) return "will-tap";
    return spec.values[0].need.sources.some((s) => oids.includes(s.object_id)) ? "payable" : "";
  }

  private clickInPayment(oids: number[]): boolean {
    const spec = this.choiceSpec();
    if (spec?.kind === "ChooseToTap" && !this.view.autoPaying) {
      const candidate = spec.values[0].candidates.find((c) => oids.includes(c.object_id) && !this.tapPicks.includes(c.object_id))
        ?? spec.values[0].candidates.find((c) => oids.includes(c.object_id));
      if (!candidate) return false;
      this.toggleTapPick(candidate.object_id);
      return true;
    }
    if (spec?.kind !== "ChoosePayment" || this.view.autoPaying) return false;
    const source = spec.values[0].need.sources.find((s) => oids.includes(s.object_id));
    if (!source) return false;
    const picked = this.payPicks.find((t) => t.object_id === source.object_id);
    if (picked) this.payPicks = this.payPicks.filter((t) => t.object_id !== source.object_id);
    else this.togglePayPick(source, 0);
    return true;
  }

  // ------------------------------------------------------------------ the pregame

  private inPregame(): boolean { return isPregameDecision(this.decision); }

  private pregameOf(pid: string): PregamePlayer | undefined {
    return this.view.pregame.find((p) => p.seat === pid);
  }

  private pregameLayout(): PregameLayout | null {
    const d = this.decision;
    if (!this.acting || !this.inPregame()) return null;
    if (d.kind === "ViewerChoosesStartingPlayer") return { kind: "coin-flip", seats: d.values[0] };
    const kept = this.pregameOf(this.view.seat)?.hand.kind ?? "Undecided";
    const cards = cardsIn(this.st.players[this.view.seat]?.hand ?? {});
    return kept === "Undecided" && cards.length > 0 ? { kind: "opening-hand", cards } : null;
  }

  // Before the first turn the turn chip names the pregame's stage, over the time its window has left.
  private renderPregameChip(): TemplateResult {
    const d = this.decision;
    const flipping = d.kind === "ViewerChoosesStartingPlayer" || (d.kind === "AnotherPlayerDecides" && d.values[1].kind === "ChoosingStartingPlayer");
    const countdown = this.pregameLayout() ? nothing : this.renderPregameWindow();
    return html`<span class="turn-chip">${countdown === nothing ? "Before the first turn" : countdown}
      <span class="phase-now">${flipping ? "Coin flip" : "Mulligans"}</span></span>`;
  }

  // The pregame's one window, which every player shares and no clock runs in.
  private renderPregameWindow(): TemplateResult | typeof nothing {
    const reading = this.view.clocks;
    const left = reading ? optionValue(reading.clocks.pregame_left_ms) : null;
    if (!reading || left === null) return nothing;
    return html`<span class="pregame-window" role="timer" title="A hand still undecided then is kept"
      >Opening hands close in <clock-time .ms=${left} .running=${true} .since=${reading.receivedAt}></clock-time></span>`;
  }

  // Who has kept, and with how many cards, and who has taken mulligans so far.
  private renderPregameChips(pid: string): TemplateResult | typeof nothing {
    const seat = this.pregameOf(pid);
    if (!seat || !this.inPregame()) return nothing;
    const n = this.st.players[pid]?.hand_count ?? 0;
    if (seat.hand.kind === "Kept") return html`<span class="chip kept" title="Kept an opening hand of ${n}">Kept ${n}</span>`;
    if (seat.mulligans === 0) return nothing;
    const taken = `${seat.mulligans} mulligan${seat.mulligans === 1 ? "" : "s"}`;
    return html`<span class="chip mulligans" title="Has taken ${taken}">${taken}</span>`;
  }

  // The pregame (103) as on Arena: the table dims under the coin flip's choice, or the viewer's opening
  // hand laid out large across the middle, with the answers beneath.
  private renderMulligan(): TemplateResult | typeof nothing {
    const layout = this.pregameLayout();
    if (!layout) return nothing;
    return layout.kind === "coin-flip" ? this.renderCoinFlip(layout.seats) : this.renderOpeningHand(layout.cards);
  }

  private renderCoinFlip(seats: string[]): TemplateResult {
    const me = this.view.seat;
    const others = seats.filter((seat) => seat !== me);
    const onePlayer = others.length === 1;
    return html`<div class="mulligan" role="dialog" aria-label="Coin flip">
      <div class="mulligan-head">
        <h2>You won the coin flip</h2>
        <p>Choose who takes the first turn. ${this.renderPregameWindow()}</p>
      </div>
      <div class="mulligan-answers">
        ${seats.includes(me) ? html`<button class="primary" aria-label="Play" @click=${() => this.intents?.chooseStartingPlayer(me)}>Play<span class="hint">Take the first turn</span></button>` : nothing}
        ${others.map((seat) => {
          const name = playerName(this.st, seat);
          const label = onePlayer ? "Draw" : `${name} plays first`;
          return html`<button class="primary alt" aria-label=${label} @click=${() => this.intents?.chooseStartingPlayer(seat)}>${onePlayer ? "Draw" : `${name} first`}<span class="hint">${onePlayer ? `${name} plays first` : "Takes the first turn"}</span></button>`;
        })}
      </div>
      ${this.renderPregameLeave()}
    </div>`;
  }

  private renderOpeningHand(cards: PublicCard[]): TemplateResult {
    const d = this.decision;
    const need = d.kind === "ViewerPutsOnBottom" ? d.values[0] : null;
    const taken = this.pregameOf(this.view.seat)?.mulligans ?? 0;
    const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
    const title = d.kind === "ViewerKeepsOrMulligans" ? "Keep this hand?"
      : need !== null ? `Put ${plural(need, "card")} on the bottom` : "Your opening hand";
    const detail = need !== null ? "Click the cards that go under your library."
      : d.kind === "AnotherPlayerDecides" ? `Waiting for ${playerName(this.st, d.values[0])} to ${waitingLabel(d.values[1].kind)}.`
      : taken > 0 ? `${plural(taken, "mulligan")} so far.` : "";
    return html`<div class="mulligan" role="dialog" aria-label="Opening hand">
      <div class="mulligan-head">
        <h2>${title}</h2>
        <p>${detail} ${this.renderPregameWindow()}</p>
      </div>
      <div class="opening-hand">
        ${repeat(cards, (c) => c.object_id, (c, i) => {
          const oid = c.object_id;
          const picked = this.bottomPicks.has(oid);
          return html`<div class="opening-card ${need === null ? "" : "pickable"} ${picked ? "bottomed" : ""}" style="--i:${i}"
              role=${need === null ? nothing : "button"} aria-pressed=${need === null ? nothing : String(picked)} aria-label=${need === null ? nothing : `Put ${c.characteristics.name} on the bottom`}
              @mouseenter=${(e: MouseEvent) => this.hover(oid, e)} @mouseleave=${() => { if (this.hoverOid === oid) this.hover(null); }}
              @click=${need === null ? null : () => this.toggleBottomPick(oid)}>
            ${this.cardArt(c)}
            ${picked ? html`<span class="to-bottom">Bottom</span>` : nothing}
          </div>`;
        })}
      </div>
      <div class="mulligan-answers">${this.renderOpeningHandAnswers(cards.length, need)}</div>
      ${this.renderPregameLeave()}
    </div>`;
  }

  private renderOpeningHandAnswers(handSize: number, need: number | null): TemplateResult | typeof nothing {
    const d = this.decision;
    if (need !== null) {
      const picks = [...this.bottomPicks];
      return html`<button class="primary" aria-label="Put on bottom" ?disabled=${picks.length !== need}
        @click=${() => this.intents?.bottomCards(picks)}>Put on bottom<span class="hint">${picks.length} of ${need} chosen</span></button>`;
    }
    if (d.kind !== "ViewerKeepsOrMulligans") return nothing;
    const bottom = d.values[0];
    return html`
      <button class="primary alt" aria-label="Mulligan" @click=${() => this.intents?.mulligan()}>Mulligan<span class="hint">${bottom === 0 ? "Draw a new 7, free" : `Draw 7, put ${bottom} on the bottom`}</span></button>
      <button class="primary" aria-label="Keep" @click=${() => this.intents?.keepHand()}>Keep<span class="hint">Start with these ${handSize}</span></button>`;
  }

  // The table's own Leave sits under the dimmed table while the pregame asks.
  private renderPregameLeave(): TemplateResult {
    return html`<button class="btn leave" @click=${() => this.intents?.leaveGame()}>Leave game</button>`;
  }

  private toggleBottomPick(oid: number): void {
    const next = new Set(this.bottomPicks);
    if (next.has(oid)) next.delete(oid); else next.add(oid);
    this.bottomPicks = next;
  }

  // ------------------------------------------------------------------ questions on the board

  private renderPop(): TemplateResult | typeof nothing {
    if (!this.acting) return nothing;
    const spec = this.choiceSpec();
    if (spec && !this.view.autoPaying) return this.choiceHidden() ? nothing : this.renderChoice(spec);
    if (this.cardMenu) return this.renderCardMenu(this.cardMenu);
    const loop = optionValue(this.st.repeatable_loop);
    if (this.loopPromptOpen && loop && this.decision.kind === "ViewerActs") return this.renderLoopPrompt(loop);
    return nothing;
  }

  // 732.2: back where a loop of theirs began, the viewer names how many more times round to take it,
  // instead of going round by hand.
  private renderLoopPrompt(loop: RepeatableLoop): TemplateResult {
    const others = loop.asks_others ? " The other players in it may stop it sooner." : "";
    return html`<div class="pop prompt" role="dialog" aria-label="Repeat the loop">
      <h3>Repeat the loop you just went round</h3>
      <p class="sub">Its ${loop.answers} answers, made again each time round, up to ${loop.most} times.${others}</p>
      <div class="input-row">
        <input id="loop-input" type="number" aria-label="Times round" min="1" max=${loop.most} .value=${String(loop.most)}
          @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter") this.repeatLoop(loop.most); }} />
        <button class="btn submit" @click=${() => this.repeatLoop(loop.most)}>Repeat</button>
      </div>
      <div class="pop-foot">
        <button class="btn" title="Close it, repeating nothing" aria-label="Cancel" @click=${() => { this.loopPromptOpen = false; }}>Cancel<kbd>Esc</kbd></button>
      </div>
    </div>`;
  }

  private repeatLoop(most: number): void {
    const el = this.renderRoot.querySelector<HTMLInputElement>("#loop-input");
    const n = el ? parseInt(el.value, 10) : most;
    this.loopPromptOpen = false;
    this.intents?.repeatLoop(Math.max(1, Math.min(most, Number.isFinite(n) ? n : 1)));
  }

  // A card with more than one way to use it asks which, and a double-faced card played asks which
  // face; one that takes targets then aims.
  private renderCardMenu(menu: { oid: number; ways: Offer[][] }): TemplateResult {
    const st = this.st;
    const faces = this.facesToPick(menu.oid, menu.ways);
    if (faces) {
      return renderFacePicker(faces, menu.ways, this.refusalsOn(menu.oid), {
        faceArt: (name) => this.faceArt(name),
        wayLabel: (way, face) => this.faceWayLabel(way, face),
        useWay: (way) => this.useWay(menu.oid, way),
        foot: this.renderPopFoot(false),
      });
    }
    const name = lookupCardNameAnywhere(st, menu.oid);
    const card = findCardAnywhere(st, menu.oid);
    const oracleText = this.oracle.get(name)?.oracle_text ?? "";
    const abilityOptions = abilityLines(card && knownFace(card)).map((line) => line ?? `${name}'s ability`);
    if (this.isPlaneswalker(card) && abilityOptions.length > 1 && menu.ways.every((way) => offerAction(way[0]!).kind === "ActivateAbility")) {
      const available = new Map<number, Offer[]>();
      for (const way of menu.ways) {
        const action = offerAction(way[0]!);
        if (action.kind === "ActivateAbility") available.set(action.values[1], way);
      }
      const refused = new Map<number, string>();
      for (const refusal of this.refusalsOn(menu.oid)) {
        if (refusal.action.kind === "ActivateAbility") refused.set(refusal.action.values[1], refusal.reason);
      }
      return renderChooseOne({
        label: `Choose an ability of ${name}`,
        title: "Choose one",
        sub: "Click an option below to select it.",
        classes: "card-menu planeswalker-menu",
        options: abilityOptions.map((line, i) => {
          const way = available.get(i);
          const disabled = !way;
          // The engine refuses each way it won't take with its reason, a loyalty cost too big among them.
          const reason = refused.get(i);
          const unavailable = reason ? `${sentence(reason)}.` : "This ability is unavailable right now.";
          return renderOption(
            this.renderAbilityOnCard(card, "Ability", line),
            `${disabled ? "Unavailable ability: " : ""}${name}: ${line}`,
            "",
            () => { if (way) this.useWay(menu.oid, way); },
            (e) => this.hover(e ? menu.oid : null, e ?? undefined),
            disabled,
            disabled ? unavailable : undefined,
          );
        }),
        foot: this.renderPopFoot(false),
      });
    }
    // A cast the engine refuses shows beside the ways it offers, greyed, with its reason: the normal
    // cast first, as it is when both are offered.
    const refused = this.castsRefusedBeside(menu.oid, menu.ways).map((r) => {
      const way: Offer[] = [{ kind: "Untargeted", values: [r.action] }];
      return { normal: r.action.kind === "CastSpell", option: renderOption(this.wayPicture(way, name, card, oracleText),
        `Unavailable: ${this.wayOptionLabel(way, card, oracleText)}`, this.wayCaption(way),
        () => {}, (e) => this.hover(e ? menu.oid : null, e ?? undefined), true, `${sentence(r.reason)}.`) };
    });
    return renderChooseOne({
      label: name,
      title: "Choose one",
      sub: `Pick how to use ${name}.`,
      classes: "card-menu",
      options: [
        ...refused.filter((r) => r.normal).map((r) => r.option),
        ...menu.ways.map((way) => renderOption(this.wayPicture(way, name, card, oracleText), this.wayOptionLabel(way, card, oracleText), this.wayCaption(way),
          () => this.useWay(menu.oid, way), (e) => this.hover(e ? menu.oid : null, e ?? undefined))),
        ...refused.filter((r) => !r.normal).map((r) => r.option),
      ],
      foot: this.renderPopFoot(false),
    });
  }

  // A way that plays or casts the card shows the card, or the face it's cast as: an Adventure (715.3)
  // or a split card's half (709.3), as its Oracle row names that face. An ability shows as Arena's
  // ability card.
  private wayPicture(way: Offer[], name: string, card: PublicCard | null, oracleText: string): TemplateResult {
    const a = offerAction(way[0]!);
    const frame = card ? frameOf(card) : "colorless";
    if (a.kind === "ActivateAbility") {
      const line = abilityLine(oracleText, card && knownFace(card), "activated", a.values[1]) ?? this.wayLabel(way);
      return card ? this.renderAbilityOnCard(card, "Ability", line) : renderAbilityCard(name, "Ability", line, frame);
    }
    if (a.kind === "Cycle") {
      const line = abilityLine(oracleText, null, "cycling", 0) ?? "Cycling";
      return card ? this.renderAbilityOnCard(card, "Ability", line) : renderAbilityCard(name, "Ability", line, frame);
    }
    const castAs = a.kind === "CastAlternative" ? altCastLabel(this.st, this.view.seat, a.values[0], a.values[2]) : null;
    if (castAs && this.oracle.get(castAs)) return this.faceArt(castAs);
    return card ? this.cardArt(card) : html`<card-art name=${name}></card-art>`;
  }

  // A way's option names the ability it activates where its Oracle line tells, as two abilities
  // are otherwise both "Activate <card>'s ability".
  private wayOptionLabel(way: Offer[], card: PublicCard | null, oracleText: string): string {
    const a = offerAction(way[0]!);
    const line = a.kind === "ActivateAbility" ? abilityLine(oracleText, card && knownFace(card), "activated", a.values[1]) : null;
    return line ? `${this.wayLabel(way)} (${line})` : this.wayLabel(way);
  }

  // What a way does, said under its card, which names the card already.
  private wayCaption(way: Offer[]): string {
    const a = offerAction(way[0]!);
    const aims = takesTargets(way) ? "…" : "";
    switch (a.kind) {
      case "CastSpell": return `Cast${aims}`;
      case "CastAlternative": return `Cast (${altCastLabel(this.st, this.view.seat, a.values[0], a.values[2])})${aims}`;
      case "CastBackFace": return `Cast${aims}`;
      case "ActivateAbility": return `Activate${aims}`;
      case "Cycle": return "Cycle";
      case "Suspend": return "Suspend";
      case "Plot": return "Plot";
      case "PlayLand": return "Play";
      default: return this.wayLabel(way);
    }
  }

  private wayLabel(way: Offer[]): string {
    const a = offerAction(way[0]!);
    return takesTargets(way) ? `${actionHowLabel(this.st, this.view.seat, a)}…` : actionLabel(this.st, this.view.seat, a);
  }

  // A way that plays a face says it by that face's name.
  private faceWayLabel(way: Offer[], face: string): string {
    const a = offerAction(way[0]!);
    if (a.kind === "PlayLand" || a.kind === "PlayLandBack") return `Play ${face}`;
    if (a.kind === "CastSpell" || a.kind === "CastBackFace") return `Cast ${face}${takesTargets(way) ? "…" : ""}`;
    return this.wayLabel(way);
  }

  // A face by its name, read from its Oracle text where its image is missing.
  private faceArt(name: string): TemplateResult {
    const oracle = this.oracle.get(name);
    return html`<card-art name=${name} typeLine=${oracle?.type_line ?? ""} oracleText=${oracle?.oracle_text ?? ""}></card-art>`;
  }

  // Every popup ends with its way out: Cancel, as a press outside it or Esc does, or for a question
  // the Room won't take back, why it must be answered.
  private renderPopFoot(asked: boolean): TemplateResult {
    const cancel = (title: string, run: () => void) => html`<div class="pop-foot">
      <button class="btn" title=${title} aria-label="Cancel" @click=${run}>Cancel<kbd>Esc</kbd></button>
    </div>`;
    if (!asked) return cancel("Close it, playing nothing", () => { this.cardMenu = null; });
    if (this.undoableActions() > 0) return cancel("Take back what you did that asked this", () => { this.cancelQuestion(); });
    const refusal = this.view.undoable ? optionValue(this.view.undoable.refusal) : null;
    return html`<div class="pop-foot">
      <span class="forced"><span class="forced-tag">Must answer</span>${sentence(refusal ?? "it can't be taken back")}.</span>
    </div>`;
  }

  private renderChoice(spec: PublicChoiceSpec): TemplateResult | typeof nothing {
    const st = this.st;
    switch (spec.kind) {
      case "ChooseTriggerOrder": {
        const triggers = spec.values[0];
        if (this.planAnsweredIn === triggers || this.plannedTriggerIndex(triggers) !== null) return nothing;
        return renderTriggerOrder({
          triggerCard: (t) => this.renderAbilityOnCard(t.source, "Trigger", this.abilityText(t.source, "triggered", t.index)),
          hover: (oid, e) => this.hover(oid, e),
          ordering: this.ordering?.question === triggers ? this.ordering : freshOrdering(triggers),
          setOrdering: (ordering) => { this.ordering = ordering; },
          putOnStackInOrder: (order) => this.putTriggersOnStack(triggers, order),
          foot: this.renderPopFoot(true),
          peek: this.renderPeek(spec),
          peeking: this.peekingAt === spec,
        });
      }
      case "ChooseArrangement": {
        const a = spec.values[0];
        const piles = this.arrangedPiles(a);
        return renderArrangement(a, {
          cardArt: (c) => this.cardArt(c),
          cardBack: this.looksOf(this.view.seat).card_back,
          piles,
          setPiles: (next) => { this.arranged = { question: a, piles: next }; },
          press: (card, e) => { if (e.button === 0) this.pressed = { oid: card, x: e.clientX, y: e.clientY, kind: "arrange" }; },
          lifted: this.dragging?.kind === "arrange" ? this.dragging.oid : null,
          answer: () => this.answer({ kind: "ChoseArrangement", values: [piles] }),
          foot: this.renderPopFoot(true),
          peek: this.renderPeek(spec),
          peeking: this.peekingAt === spec,
        });
      }
      case "ChooseFromRevealed":
      case "MustChooseFromRevealed": {
        const cards = spec.values[0];
        return renderChooseOne({
          label: "Choose one",
          title: "Choose one",
          sub: cards.length === 0 ? "No legal candidates were revealed." : spec.kind === "ChooseFromRevealed" ? "Pick a card, or decline." : "Pick a card.",
          options: cards.map((c, i) => renderOption(this.cardArt(c), `Choose ${c.characteristics.name}`, c.characteristics.name,
            () => this.answer({ kind: "ChoseFromRevealed", values: [{ kind: "Some", values: [i] }] }), (e) => this.hover(e ? c.object_id : null, e ?? undefined))),
          hide: () => this.hideChoice(),
          foot: this.renderPopFoot(true),
        });
      }
      // The engine's "one of these", whatever asks it (a mode, a color, an offer), so only its words.
      case "ChooseMode":
        return renderChooseOne({
          label: "Choose one",
          title: "Choose one",
          sub: "Pick one.",
          options: [renderChoiceList(spec.values[0], (i) => this.answer({ kind: "ChoseMode", values: [i] }))],
          aside: this.askerArt(),
          hide: () => this.hideChoice(),
          foot: this.renderPopFoot(true),
        });
      case "ChooseOptional":
        return this.renderOptional(spec.values[0]);
      case "ChooseInt": {
        const [min, max] = spec.values;
        return html`<div class="pop prompt" role="dialog" aria-label="Choose a number">
          <h3>Choose X, from ${min} to ${max}</h3>
          <div class="input-row">
            <input id="x-input" type="number" aria-label="X" min=${min} max=${max} .value=${String(max)}
              @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter") this.submitNumber(min, max); }} />
            <button class="btn submit" @click=${() => this.submitNumber(min, max)}>Submit</button>
          </div>
          ${this.renderPopFoot(true)}
        </div>`;
      }
      case "ChooseName":
      case "ChooseNonlandCardName":
      case "ChooseCreatureType":
        return this.renderNamePicker(spec);
      case "ChooseDamageAssignment": return this.renderDamageAssignment(spec.values[0]);
      case "ChoosePayment": return this.renderPayment(spec.values[0]);
      case "ChooseToTap": return this.renderTapPayment(spec.values[0]);
      case "ChooseCard":
        return this.renderCardChoice(spec);
      // Asked on the board itself: the candidates glow where they are (renderOffBoardChoices).
      case "ChooseTarget":
        return nothing;
    }
  }

  private renderCardChoice(spec: PublicChoiceSpec): TemplateResult | typeof nothing {
    const oids = this.overlaidCardChoice(spec);
    if (!oids) return nothing;
    const title = spec.kind === "ChooseCard" && spec.values[0] ? spec.values[0] : "Choose a card";
    return renderChooseOne({
      label: title,
      title,
      sub: cardCount(oids.length),
      classes: `card-choice ${this.peekingAt === spec ? "peeking" : ""}`,
      optionsClass: "off-board-choices",
      options: oids.map((oid) => {
        const c = this.cardFor(oid);
        const label = this.cardChoiceLabel(oid);
        return renderOption(c ? this.cardArt(c) : html`<card-art></card-art>`, `Choose ${label}`, c && label === cardLabel(c) ? "" : label,
          () => { this.pickCard(oid); }, (e) => this.hover(e ? oid : null, e ?? undefined));
      }),
      foot: html`<div class="choose-buttons">${this.renderPeek(spec)}</div>${this.renderPopFoot(true)}`,
    });
  }

  private overlaidCardChoice(spec: PublicChoiceSpec | null): number[] | null {
    if (spec?.kind !== "ChooseCard") return null;
    const oids = spec.values[1];
    return oids.length > 0 && oids.every((oid) => !this.onBattlefieldOrStack(oid)) ? oids : null;
  }

  private onBattlefieldOrStack(oid: number): boolean {
    const key = String(oid);
    return Object.values(this.st.players).some((p) => p.battlefield[key] !== undefined)
      || this.st.stack.some((item) => stackItemObjectId(item) === oid);
  }

  private renderPeek(spec: PublicChoiceSpec): TemplateResult {
    const peeking = this.peekingAt === spec;
    return html`<button class="btn peek" aria-pressed=${peeking ? "true" : "false"} @click=${() => { this.peekingAt = peeking ? null : spec; }}>
      ${peeking ? "Back to the choice" : "View battlefield"}</button>`;
  }

  private triggerKey(t: PublicTrigger): string {
    return `${t.source.object_id}:${t.index}`;
  }

  private plannedTriggerIndex(triggers: PublicTrigger[]): number | null {
    if (this.triggerPlan.length === 0 || this.triggerPlanTurn !== this.st.turn) return null;
    const asked = triggers.map((t) => this.triggerKey(t)).sort();
    const planned = [...this.triggerPlan].sort();
    if (asked.length !== planned.length || asked.some((k, i) => k !== planned[i])) return null;
    const next = triggers.findIndex((t) => this.triggerKey(t) === this.triggerPlan[0]);
    return next === -1 ? null : next;
  }

  private putTriggersOnStack(triggers: PublicTrigger[], order: number[]): void {
    this.triggerPlan = order.slice(1).map((i) => this.triggerKey(triggers[i]!));
    this.triggerPlanTurn = this.st.turn;
    this.planAnsweredIn = triggers;
    this.ordering = null;
    this.answer({ kind: "ChoseMode", values: [order[0]!] });
  }

  private answerPlannedTrigger(): void {
    const spec = this.choiceSpec();
    if (!this.acting || spec?.kind !== "ChooseTriggerOrder" || this.planAnsweredIn === spec.values[0]) return;
    const next = this.plannedTriggerIndex(spec.values[0]);
    if (next === null) { this.triggerPlan = []; return; }
    this.planAnsweredIn = spec.values[0];
    this.triggerPlan = this.triggerPlan.slice(1);
    this.answer({ kind: "ChoseMode", values: [next] });
  }

  // A "you may" or an optional cost: yes or no now, or the same answer whenever this card asks, which
  // the Room then gives for the viewer this game, this question included.
  private renderOptional(q: OptionalChoice): TemplateResult {
    const yes: AlwaysAnswer = { kind: "AlwaysYes", values: [] };
    const no: AlwaysAnswer = { kind: "AlwaysNo", values: [] };
    return renderChooseOne({
      label: `${q.card} asks`,
      title: q.card,
      sub: "Yes or no, now. Always yes or Always no answers it the same way whenever it asks, this game.",
      classes: "optional",
      options: [renderChoiceList([q.take, q.decline], (i) => this.answer({ kind: "ChoseMode", values: [i] }))],
      aside: this.askerArt() ?? html`<card-art name=${q.card}></card-art>`,
      hide: () => this.hideChoice(),
      foot: html`<div class="choices always">
          <button class="btn" title="${q.take}, now and whenever ${q.card} asks this game" @click=${() => this.setAutoAnswer(q.card, yes)}>Always yes</button>
          <button class="btn" title="${q.decline}, now and whenever ${q.card} asks this game" @click=${() => this.setAutoAnswer(q.card, no)}>Always no</button>
        </div>${this.renderPopFoot(true)}`,
    });
  }

  private askerArt(): TemplateResult | undefined {
    const asker = this.askingCard();
    return asker ? this.cardArt(asker) : undefined;
  }

  private renderNamePicker(q: NameQuestion): TemplateResult {
    return html`<div class="pop prompt" role="dialog" aria-label=${nameQuestionTitle(q)}>
      <h3>${nameQuestionTitle(q)}</h3>
      <name-picker .options=${this.nameLists.offeredFor(q)} ?unavailable=${this.nameLists.failed} noun=${nameQuestionNoun(q)}
        @name-picked=${(e: CustomEvent<string | null>) => { this.namePick = e.detail; }}
        @name-confirmed=${(e: CustomEvent<string>) => this.answerName(e.detail)}></name-picker>
      ${this.renderPopFoot(true)}
    </div>`;
  }

  private combatCard(c: PublicCard): TemplateResult {
    return html`${this.cardArt(c)}${this.renderPt(c)}${this.renderBadges(c, controllerOf(this.st, c.object_id) ?? "", 1, false)}`;
  }

  private renderDamageAssignment(a: DamageAssignment): TemplateResult {
    const st = this.st;
    const attacker = findOnAnyBattlefield(st, a.attacker);
    const name = attacker ? cardLabel(attacker) : combatLabel(st, a.attacker);
    const trampleTo = optionValue(a.trample_to);
    const trample = trampleTo ? ` The rest tramples over to ${targetNameLabel(st, trampleTo)}.` : "";
    const lifted = this.dragging?.kind === "damage" ? this.dragging.oid : null;
    const toggle = html`<button class="btn view-battlefield" @click=${() => { this.viewingBattlefield = !this.viewingBattlefield; }}>
      ${this.viewingBattlefield ? "Order damage" : "View battlefield"}</button>`;
    if (this.viewingBattlefield) return html`<div class="damage-peek">${toggle}</div>`;
    const done = () => this.answer({ kind: "ChoseDamageAssignment", values: [damageInOrder(a, this.damageOrder)] });
    return renderChooseOne({
      label: `Order ${name}'s combat damage`,
      title: "Order Damage",
      sub: `Drag blockers to choose who ${name} hits first.${trample}`,
      classes: "damage",
      options: [html`<div class="assign">
        ${attacker ? html`<div class="attacker">${this.combatCard(attacker)}</div>` : nothing}
        <div class="tray">
          <div class="order" role="list" aria-label="${name}'s blockers, first hit first">
          ${this.damageOrder.map((oid) => {
            const c = findOnAnyBattlefield(st, oid);
            return html`<div class="damage-to ${lifted === oid ? "lifted" : ""}" role="listitem" tabindex="0" data-blocker=${oid}
                aria-label=${c ? cardLabel(c) : `#${oid}`} aria-keyshortcuts="ArrowLeft ArrowRight"
                @pointerdown=${(e: PointerEvent) => { if (e.button === 0) this.pressed = { oid, x: e.clientX, y: e.clientY, kind: "damage" }; }}
                @keydown=${(e: KeyboardEvent) => {
                  const by = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
                  if (by !== 0) { e.preventDefault(); this.moveDamageBlocker(oid, by); }
                }}>${c ? this.combatCard(c) : nothing}</div>`;
          })}
          </div>
          ${this.damageOrder.length > 1 ? html`<div class="ends" aria-hidden="true"><span>First</span><span>Last</span></div>` : nothing}
        </div>
      </div>`],
      foot: html`<div class="pop-foot done"><button class="btn" @click=${done}>Done</button></div>${this.undoableActions() > 0 ? this.renderPopFoot(true) : nothing}${toggle}`,
    });
  }

  // 601.2g-h: prefilled with auto-pay's taps, so the big button pays in one click. Each source's
  // ways to pay are here, and the sources themselves glow on the battlefield.
  private renderPayment(payment: ManaPayment): TemplateResult {
    const need = payment.need;
    const floating = manaText(need.pool);
    const picked = (s: ManaSource, k: number) =>
      this.payPicks.some((t) => t.object_id === s.object_id && sameAbility(t.ability, s.ability) && t.output === k);
    return html`<div class="pop prompt payment" role="dialog" aria-label="Pay ${costText(need.cost)}">
      <h3>Pay ${costText(need.cost)}</h3>
      ${floating ? html`<div class="sub">${floating} floating pays first.</div>` : nothing}
      <div class="sub">Tap these, or pick others here or on the battlefield:</div>
      ${need.sources.map((s) => html`<div class="pay-row">
        <span class="pay-source">${s.name} <span class="muted">${sourceCostHint(s.cost)}</span></span>
        ${s.outputs.map((o, k) => html`<button class="btn ${picked(s, k) ? "picked" : ""}" aria-pressed=${picked(s, k) ? "true" : "false"} @click=${() => this.togglePayPick(s, k)}>
          ${manaText(o.mana)}${o.spend_only.kind === "Some" ? ` ${spendOnlyHint(o.spend_only.values[0])}` : ""}</button>`)}
      </div>`)}
      ${this.renderPopFoot(true)}
    </div>`;
  }

  // 702.122a: prefilled with the suggestion, so the big button taps in one click. Each creature that
  // may be tapped is here with its power, and glows on the battlefield too. 702.51a, 702.126a: for
  // convoke and improvise, each says what it may pay for, and mana pays the rest; 701.67b: for a
  // waterbend cost, how many may be tapped at most.
  private renderTapPayment(payment: TapPayment): TemplateResult {
    const source = this.cardFor(payment.source);
    const total = tappedTotal(payment, this.tapPicks);
    const part = optionValue(payment.part_of);
    const most = part ? optionValue(part.most_tapped) : undefined;
    const sub = part
      ? most != null
        ? `Tap up to ${most} of these for {1} each, here or on the battlefield; mana pays the rest of ${costText(part.cost)}: ${this.tapPicks.length} so far.`
        : `Tap any of these for one mana of ${costText(part.cost)} each, here or on the battlefield; mana pays the rest: ${this.tapPicks.length} so far.`
      : `Tap creatures with total power ${payment.at_least} or more, here or on the battlefield: ${total} so far.`;
    return html`<div class="pop prompt payment" role="dialog" aria-label="Pay ${payment.cost}">
      <h3>${payment.cost}${source ? html` <span class="muted">${cardLabel(source)}</span>` : nothing}</h3>
      <div class="sub">${sub}</div>
      ${payment.candidates.map((c) => {
        const card = this.cardFor(c.object_id);
        const picked = this.tapPicks.includes(c.object_id);
        const pays = part ? tapperPays(part.tappers.find((t) => t.object_id === c.object_id)) : `power ${c.counts}`;
        return html`<div class="pay-row">
          <span class="pay-source">${card ? cardLabel(card) : `#${c.object_id}`} <span class="muted">${pays}</span></span>
          <button class="btn ${picked ? "picked" : ""}" aria-pressed=${picked ? "true" : "false"} @click=${() => this.toggleTapPick(c.object_id)}>Tap</button>
        </div>`;
      })}
      ${this.renderPopFoot(true)}
    </div>`;
  }

  private toggleTapPick(oid: number): void {
    this.tapPicks = this.tapPicks.includes(oid) ? this.tapPicks.filter((x) => x !== oid) : [...this.tapPicks, oid];
  }

  // One output per source: picking another of a source's outputs replaces the first.
  private togglePayPick(source: ManaSource, output: number): void {
    const same = (t: ManaTap) => t.object_id === source.object_id && sameAbility(t.ability, source.ability) && t.output === output;
    const others = this.payPicks.filter((t) => t.object_id !== source.object_id);
    this.payPicks = this.payPicks.some(same) ? others : [...others, { object_id: source.object_id, ability: source.ability, output }];
  }

  // ------------------------------------------------------------------ drawers and previews

  // Newest first: each turn, then each step anyone had priority in or answered in, with who passed
  // there, then what was done in it.
  private renderLog(): TemplateResult {
    if (!this.logOpen) return html`<button class="drawer-tab" title="Game log (L)" @click=${() => { this.logOpen = true; }}>Log <kbd>L</kbd></button>`;
    const st = this.st;
    const turns = loggedTurns(st, this.view.history);
    return html`<div class="drawer" role="log" aria-label="Game log">
      <header><span>Game log</span><button @click=${() => { this.logOpen = false; }}>Close</button></header>
      <div class="lines">
        ${turns.length === 0 ? html`<div class="log-empty">No actions yet.</div>` : turns.slice().reverse().map((t) => html`
          <div class="log-turn">${t.turn === null ? "Before the first turn"
            : `Turn ${t.round} · ${t.active === this.view.seat ? "Your turn" : `${playerName(st, t.active)}'s turn`}`}</div>
          ${t.steps.slice().reverse().map((s) => html`
            ${t.turn === null ? nothing : html`<div class="log-step"><span>${PHASE_LABEL[s.phase]}</span>${s.passed.length > 0
              ? html`<span class="passes">${this.passedLine(s.passed)}</span>` : nothing}</div>`}
            ${s.lines.slice().reverse().map((l) => html`
              <div class="log-line"><span class="actor">${this.actorLabel(l)}</span><span>${this.logWords(l)}</span></div>`)}`)}`)}
      </div>
    </div>`;
  }

  private renderMenu(): TemplateResult {
    if (!this.menuOpen) return html`<button class="drawer-tab at-right" title="Game menu" @click=${() => { this.menuOpen = true; }}>Menu</button>`;
    return html`<div class="drawer at-right" role="dialog" aria-label="Game menu">
      <header><span>Game menu</span><button @click=${() => { this.menuOpen = false; }}>Close</button></header>
      <div class="menu-body">
        <section class="menu-game">
          <span class="game-id" title="This game's id">Game <code>${this.view.game}</code></span>
          <div class="menu-actions">
            ${this.renderConcede()}
            ${this.intents ? html`<button class="btn leave" @click=${() => this.intents?.leaveGame()}>Leave</button>` : nothing}
          </div>
        </section>
        <slot name="menu"></slot>
      </div>
    </div>`;
  }

  private passedLine(seats: string[]): string {
    const names = seats.map((seat) => seat === this.view.seat ? "you" : playerName(this.st, seat));
    const joined = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
    return `${sentence(joined)} passed`;
  }

  // A card in a graveyard or exile the viewer can play from there now.
  private usableFromZone(oid: number): boolean {
    return this.acting && this.actionsOn(oid).length > 0;
  }

  // 400.2: graveyard and exile are public, so either side's may be viewed. A card the viewer can play
  // from there now glows, and is played with a click, as from the hand.
  private renderZoneShown(): TemplateResult | typeof nothing {
    const shown = this.zoneShown;
    const p = shown ? this.st.players[shown.pid] : undefined;
    if (!shown || !p) return nothing;
    const cards = cardsIn(shown.zone === "graveyard" ? p.graveyard : p.exile);
    const zone = shown.zone === "graveyard" ? "Graveyard" : "Exile";
    const close = () => { this.zoneShown = null; };
    return renderChooseOne({
      label: `${p.name}'s ${shown.zone}`,
      title: `${shown.pid === this.view.seat ? "Your" : `${p.name}'s`} ${zone}`,
      sub: cardCount(cards.length),
      classes: "zone-viewer",
      style: `--n: ${cards.length}`,
      options: cards.map((c) => {
        const usable = this.usableFromZone(c.object_id);
        return html`<div class="zv-card ${usable ? "playable" : ""}" role=${usable ? "button" : nothing} aria-label=${usable ? `Play ${cardLabel(c)}` : nothing}
            title=${usable ? `${cardLabel(c)}: click to play` : nothing}
            @mouseenter=${(e: MouseEvent) => this.hover(c.object_id, e)} @mouseleave=${() => this.hover(null)}
            @click=${usable ? () => { this.zoneShown = null; this.useCard(c.object_id); } : null}>
          ${c.on_an_adventure ? html`<span class="kind Adventure" title="Exiled as its Adventure resolved: whoever cast that may cast it from here">On an adventure</span>` : nothing}
          ${this.cardArt(c)}</div>`;
      }),
      foot: html`<div class="pop-foot"><button class="btn done" title="Close it (Esc)" @click=${close}>Done</button></div>`,
    });
  }

  // The card asking the open question or whose target is being chosen stays visible beside the
  // hovered card, so the source and the thing being pointed at can both be read.
  private renderPreview(): TemplateResult {
    const targetSource = this.aim ? this.cardFor(this.aim.source) : null;
    const asker = targetSource ?? (this.askerBesideChoice() ? null : this.askingCard());
    const askerSide = this.view.previewAt === "left" ? "at-left" : "at-right";
    const hovered = this.hoverOid === null ? null : this.cardFor(this.hoverOid);
    const hoverSide = this.view.previewAt === "middle" ? "at-middle"
      : asker ? (askerSide === "at-right" ? "at-left" : "at-right")
      : this.view.previewAt === "auto" ? this.previewSide : `at-${this.view.previewAt}`;
    const targeting = Boolean(targetSource);
    const ability = this.hoverOid === null ? null : this.abilityOnStack(this.hoverOid);
    return html`${asker ? this.renderCardPanel(asker, askerSide, true, targeting) : nothing}
      <div class="hover-preview" style="--preview-zoom: ${this.previewZoom}">${ability ? this.renderAbilityPanel(ability, hoverSide)
        : hovered && hovered.object_id !== asker?.object_id ? this.renderCardPanel(hovered, hoverSide, false) : nothing}</div>`;
  }

  private askerBesideChoice(): boolean {
    const kind = this.choiceSpec()?.kind;
    return (kind === "ChooseMode" || kind === "ChooseOptional") && this.popOpen() === "question";
  }

  private abilityOnStack(oid: number): PublicAbilityItem | null {
    const item = this.st.stack.find((i) => stackItemObjectId(i) === oid);
    return item?.kind === "AbilityItem" ? item.values[0] : null;
  }

  // An ability on the stack as Arena shows it: its source's card, with the type line saying what
  // it is and the text box holding, where its source's Oracle text tells which line it is, that line.
  private renderAbilityPanel(ability: PublicAbilityItem, side: string): TemplateResult {
    return html`<div class="card-preview ${side}">${this.abilityCard(ability)}</div>`;
  }

  private abilityCard(ability: PublicAbilityItem): TemplateResult {
    const triggered = ability.kind.kind === "TriggeredAbility";
    const text = this.abilityText(ability.source, triggered ? "triggered" : "activated", optionValue(ability.index));
    return this.renderAbilityOnCard(ability.source, triggered ? "Trigger" : "Ability", text);
  }

  private abilityText(source: PublicCard, kind: "activated" | "triggered", index: number | null): string {
    const face = knownFace(source);
    const oracleText = face ? this.oracle.get(face.name)?.oracle_text ?? "" : "";
    return (index === null ? null : abilityLine(oracleText, face, kind, index)) ?? oracleText;
  }

  private renderAbilityOnCard(source: PublicCard, kind: string, text: string): TemplateResult {
    const face = knownFace(source);
    return renderAbilityCard(face ? face.name : cardLabel(source), kind, text, frameOf(source), optionValue(source.token_art_key) ?? "");
  }

  private renderCardPanel(
    c: PublicCard,
    side: string,
    asking: boolean,
    targeting = false,
  ): TemplateResult | typeof nothing {
    const face = knownFace(c);
    if (!face) return nothing;
    const oracle = this.oracle.get(face.name);
    const typeLine = oracle?.type_line || typeLineOf(face);
    const pt = ptLabelOf(face, c.damage) || oracle?.power_toughness || "";
    const state = [c.tapped ? "tapped" : "", isCreatureCard(c) && c.entered_battlefield_turn > 0 ? `here since turn ${c.entered_battlefield_turn}` : "",
      c.on_an_adventure ? "on an adventure: whoever cast its Adventure may cast it from exile" : ""].filter(Boolean).join(" · ");
    const art = html`<card-art data-anchor=${targeting ? "aim-source" : nothing} .targetFocus=${targeting} name=${face.name} .keywords=${isFaceDown(c) ? [] : face.keywords} artKey=${optionValue(c.token_art_key) ?? ""} typeLine=${typeLine} oracleText=${oracle?.oracle_text ?? ""}></card-art>`;
    const faces = facesOf(c);
    const why = asking ? nothing : this.renderWhy(c.object_id);
    return html`<div class="card-preview ${side} ${faces ? "two-faced" : ""} ${asking ? "asking" : ""} ${targeting ? "target-source" : ""}">
      ${asking && !targeting ? html`<span class="asking-tag">Asking you</span>` : nothing}
      ${faces ? this.renderPreviewFaces(faces, art) : art}
      ${targeting ? nothing : this.altHeld ? html`<div class="preview-text">
        <div class="preview-name"><span>${cardLabel(c)}</span>${oracle?.mana_cost ? html`<span class="preview-cost">${oracle.mana_cost}</span>` : nothing}</div>
        <div class="preview-type">${typeLine}${pt ? ` · ${pt}` : ""}</div>
        ${oracle?.oracle_text ? html`<div class="preview-oracle">${oracle.oracle_text}</div>` : nothing}
        ${state ? html`<div class="preview-state">${state}</div>` : nothing}
        ${why}
        ${faces ? this.renderPreviewOtherFace(faces) : nothing}
      </div>` : why === nothing ? nothing : html`<div class="preview-text">${why}</div>`}
    </div>`;
  }

  // The engine's reason for each way the viewer can't use this card of theirs now (#202).
  private renderWhy(oid: number): TemplateResult | typeof nothing {
    const why = this.refusalsOn(oid);
    if (why.length === 0) return nothing;
    return html`<div class="why" role="note" aria-label="Why you can't">
      ${why.map((r) => html`<div class="why-line"><span class="why-how">${actionHowLabel(this.st, this.view.seat, r.action)}</span>
        <span class="why-reason">${sentence(r.reason)}.</span></div>`)}
    </div>`;
  }

  // A card the viewer clicked but can't use, with why, until the board moves on.
  private renderWhyShown(): TemplateResult | typeof nothing {
    const oid = this.whyShown;
    if (oid === null || this.unusable(oid).length === 0) return nothing;
    return html`<div class="why-shown" role="status">
      <div class="why-title">You can't use ${lookupCardNameAnywhere(this.st, oid)} now
        <button class="close" aria-label="Close" @click=${() => { this.whyShown = null; }}>✕</button></div>
      ${this.renderWhy(oid)}
    </div>`;
  }

  // A double-faced card shows both its faces, the one that's up first.
  private renderPreviewFaces(faces: PublicFaces, upArt: TemplateResult): TemplateResult {
    const up = upSide(faces);
    const other = otherSide(up);
    return html`<div class="preview-faces">
      <div class="preview-face">${upArt}<span class="face-side">${sideLabel(up)}</span></div>
      <div class="preview-face">${this.faceArt(faceName(faces, other))}<span class="face-side">${sideLabel(other)}</span></div>
    </div>`;
  }

  private renderPreviewOtherFace(faces: PublicFaces): TemplateResult {
    const other = otherSide(upSide(faces));
    const name = faceName(faces, other);
    const oracle = this.oracle.get(name);
    return html`<div class="preview-other">
      <div class="preview-name"><span><span class="preview-side">${sideLabel(other)}</span>${name}</span>${oracle?.mana_cost ? html`<span class="preview-cost">${oracle.mana_cost}</span>` : nothing}</div>
      ${oracle ? html`<div class="preview-type">${oracle.type_line}${oracle.power_toughness ? ` · ${oracle.power_toughness}` : ""}</div>` : nothing}
      ${oracle?.oracle_text ? html`<div class="preview-oracle">${oracle.oracle_text}</div>` : nothing}
    </div>`;
  }

  private renderTurnBanner(): TemplateResult | typeof nothing {
    const b = this.turnBanner;
    if (!b) return nothing;
    const mine = b.active === this.view.seat;
    return html`${keyed(b.turn, html`<div class="turn-banner ${mine ? "mine" : ""}" role="status">${mine ? "Your turn" : `${playerName(this.st, b.active)}'s turn`}</div>`)}`;
  }

  private renderError(): TemplateResult | typeof nothing {
    if (!this.view.error) return nothing;
    return html`<div class="error" role="alert">${this.view.error}<button aria-label="Dismiss" @click=${() => this.intents?.dismissError()}>✕</button></div>`;
  }
}

// A few creatures by name; past that, how many more.
function cardCount(n: number): string {
  return n === 0 ? "No cards" : n === 1 ? "1 Card" : `${n} Cards`;
}

function countsOf(counts: number[]): number[] {
  return [...new Set(counts)].sort((a, b) => a - b);
}

function countWord(n: number, capitalized: boolean): string {
  const word = ["zero", "one", "two", "three", "four", "five"][n] ?? String(n);
  return capitalized ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

function listed(state: PublicState, oids: number[]): string {
  const shown = oids.slice(0, 3).map((oid) => combatLabel(state, oid)).join(", ");
  return oids.length > 3 ? `${shown} and ${oids.length - 3} more` : shown;
}

// What the picked creatures count toward a crew cost: their power, added up (702.122a).
function tappedTotal(payment: TapPayment, picks: number[]): number {
  return payment.candidates.filter((c) => picks.includes(c.object_id)).reduce((sum, c) => sum + c.counts, 0);
}

// 702.51a, 702.126a: what tapping one pays for: {1}, or one mana of a color it is.
function tapperPays(tapper: Tapper | undefined): string {
  const pips = [...(tapper?.colors ?? []).map((c) => costText([c])), "{1}"];
  return `pays ${pips.join(" or ")}`;
}

// The objects on a battlefield or the stack `after` that weren't on one `before`, but the viewer's own.
function arrivals(before: PublicState, after: PublicState, viewer: string): Set<number> {
  const there = new Set(before.stack.map(stackItemObjectId));
  for (const p of Object.values(before.players)) for (const oid of Object.keys(p.battlefield)) there.add(Number(oid));
  const arrived = new Set<number>();
  for (const [pid, p] of Object.entries(after.players)) {
    if (pid === viewer) continue;
    for (const oid of Object.keys(p.battlefield).map(Number)) if (!there.has(oid)) arrived.add(oid);
  }
  for (const item of after.stack) {
    const oid = stackItemObjectId(item);
    if (stackItemController(item) !== viewer && !there.has(oid)) arrived.add(oid);
  }
  return arrived;
}

declare global {
  interface HTMLElementTagNameMap {
    "arena-board": ArenaBoard;
  }
}
